import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Switch,
  Select,
  Input,
  InputNumber,
  Button,
  message,
  Divider,
  Alert,
  Tooltip,
  Modal,
  Skeleton,
  Spin,
  Tag,
} from "antd";
import { AuthContext } from "../context/AuthProvider";
import { useConnectStatus } from "../hooks/useConnectStatus";
import {
  BellOutlined,
  MessageOutlined,
  SafetyOutlined,
  HomeOutlined,
  DollarOutlined,
  FileTextOutlined,
  ToolOutlined,
  RobotOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  ExclamationCircleOutlined,
  BulbOutlined,
  DeleteOutlined,
  ReloadOutlined,
  GlobalOutlined,
  EditOutlined,
  BankOutlined,
  CalendarOutlined,
  CloudUploadOutlined,
  PlayCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import SettingsModel from "../lib/firebase/settingsModel";
import { Link, useLocation } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { trackEvent } from "../helpers/mixpanel";
import { propertyKeys } from "../lib/queryKeys";
import {
  getDebugMode,
  getSettingsDemoMode,
  getDebugUnitCount,
  OPS_CHAT_PRICE_DISPLAY,
} from "../helpers/constants";
import {
  getDebugConnectAccounts,
  getDebugSettingsUnits,
  getDebugTenants,
} from "../helpers/debugData";
import logoImage from "../assets/logo.png";
import { LAYOUT_CONSTANTS, device } from "../lib/breakpoints";
import { ConnectOnboarding } from "../components/payments";
import {
  createLoginLink,
  listConnectAccounts,
  createAdditionalConnectAccount,
  createAccountLink,
  updateConnectAccount,
  deleteConnectAccount,
} from "../lib/payments/stripe";
import { useNavigate } from "react-router-dom";
import { logoutUser } from "../helpers/authentication";
import { getProperties, updateProperty } from "../lib/api/propertiesApi";
import unitModel from "../lib/firebase/unitModel";
import { listTenants } from "../lib/firebase/tenantModel";
import { DetailsModal } from "../components/shared";
import ComplianceSettings from "../components/settings/ComplianceSettings";
import LeasingShowingSettings from "../components/settings/LeasingShowingSettings";
import TenantChatSettings from "../components/settings/TenantChatSettings";
import ImportPortfolioModal from "../components/ImportPortfolioModal";
import {
  canAddUnit,
  getEffectiveUnitCap,
  getPricingSchedule,
  normalizePlanKeyForSchedule,
} from "../lib/pricing";
import { shouldShowScaleUpgradeForFullOs } from "../helpers/accountProduct";
import { hasLeoKnowledgeBaseContext } from "../helpers/opsChatSetup";

const { Option } = Select;
const { TextArea } = Input;

const Settings = () => {
  const { user, userInfo, isOpsChatOnlyUser } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [showPaymentSetup, setShowPaymentSetup] = useState(false);
  const [managingStripeAccount, setManagingStripeAccount] = useState(false);
  const [deleteAccountStep, setDeleteAccountStep] = useState(0); // 0 = hidden, 1-3 = confirmation steps
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [propertySavingId, setPropertySavingId] = useState(null);
  const [depositSavingId, setDepositSavingId] = useState(null);
  const [autoApproveDrafts, setAutoApproveDrafts] = useState({});
  const [propertyModalVisible, setPropertyModalVisible] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyModalSaving, setPropertyModalSaving] = useState(false);
  const [propertyModalEditing, setPropertyModalEditing] = useState(false);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [newAccountName, setNewAccountName] = useState("");
  const [addAccountLoading, setAddAccountLoading] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [editingAccountName, setEditingAccountName] = useState("");
  const [renameAccountLoading, setRenameAccountLoading] = useState(false);
  const [accountToRemove, setAccountToRemove] = useState(null);
  const [removeAccountLoading, setRemoveAccountLoading] = useState(false);
  const [isImportPortfolioModalOpen, setIsImportPortfolioModalOpen] =
    useState(false);
  const queryClient = useQueryClient();

  const isSettingsDemo = getDebugMode() && getSettingsDemoMode();
  const settingsDemoMode = getSettingsDemoMode();
  const debugUnitCount = getDebugUnitCount();
  const useDemoAssets =
    isSettingsDemo &&
    (settingsDemoMode === "assets" || settingsDemoMode === "both");

  const { data: propertiesData, isLoading: isPropertiesLoading } = useQuery({
    queryKey: ["properties", user?.uid],
    queryFn: getProperties,
    enabled: !!user?.uid,
  });
  const properties = propertiesData?.properties ?? [];

  const { data: unitsFromApi = [] } = useQuery({
    queryKey: propertyKeys.units(user?.uid),
    queryFn: () => unitModel.getByOwner(user.uid),
    enabled: !!user?.uid && !useDemoAssets,
  });

  const { data: tenantsData } = useQuery({
    queryKey: propertyKeys.tenants(user?.uid),
    queryFn: listTenants,
    enabled: !!user?.uid && !useDemoAssets,
  });

  const { data: connectAccountsData } = useQuery({
    queryKey: ["connectAccounts", user?.uid],
    queryFn: listConnectAccounts,
    enabled: !!user?.uid,
  });

  const demoConnectAccounts = isSettingsDemo ? getDebugConnectAccounts() : null;
  const accountsForOptionsAndGrid = demoConnectAccounts
    ? demoConnectAccounts.accounts
    : connectAccountsData?.accounts?.length > 0
    ? connectAccountsData.accounts
    : null;
  const depositAccountOptions = accountsForOptionsAndGrid
    ? accountsForOptionsAndGrid.map((a) => ({
        value: a.isPrimary ? "" : a.stripeConnectId || "",
        label: a.name,
      }))
    : [{ value: "", label: "Primary account" }];

  const units = useDemoAssets
    ? getDebugSettingsUnits(debugUnitCount)
    : unitsFromApi ?? [];
  const tenants = useDemoAssets
    ? getDebugTenants(debugUnitCount) ?? []
    : tenantsData?.tenants ?? [];
  const displayProperties = useDemoAssets ? [] : properties;
  // Leasing links are backed by real property records; in demo-assets mode keep using
  // live properties so existing links are still visible/editable.
  const leasingProperties = Array.isArray(properties) ? properties : [];

  const subscriptionTier =
    userInfo?.subscription || userInfo?.selectedPlan || "starter";
  const pricingTierGroup = userInfo?.pricingTierGroup || "market_2026";
  const tierKeyRaw = String(subscriptionTier).toLowerCase();
  const normalizedTier = normalizePlanKeyForSchedule(
    tierKeyRaw,
    pricingTierGroup
  );
  const importUnitCap = getEffectiveUnitCap(userInfo, pricingTierGroup);
  const canAddMoreUnitsForImport = canAddUnit(
    normalizedTier,
    units.length,
    pricingTierGroup,
    userInfo
  );
  const importRemainingSlots = Math.max(0, importUnitCap - units.length);

  const handleRepairAutoApproveChange = async (propertyId, value) => {
    if (!propertyId || value == null) return;
    setPropertySavingId(propertyId);
    try {
      await updateProperty(propertyId, { repairAutoApproveLimit: value });
      message.success("AI Auto-Approve Limit saved");
      queryClient.invalidateQueries({ queryKey: ["properties", user?.uid] });
    } catch (e) {
      message.error(e?.message || "Failed to save");
    } finally {
      setPropertySavingId(null);
    }
  };

  /** Save property-level deposit account override. value: "" = Primary (send null), "acct_xxx" = override. */
  const handleDepositAccountChange = async (propertyId, value) => {
    if (!propertyId) return;
    setDepositSavingId(propertyId);
    try {
      const stripeConnectIdOverride =
        value === "" || value == null ? null : value;
      await updateProperty(propertyId, { stripeConnectIdOverride });
      message.success("Deposit account saved");
      queryClient.invalidateQueries({ queryKey: ["properties", user?.uid] });
    } catch (e) {
      message.error(e?.message || "Failed to save deposit account");
    } finally {
      setDepositSavingId(null);
    }
  };

  /** Save unit-level deposit account override (Firestore) when API properties are not used. */
  const handleUnitDepositAccountChange = async (unitId, value) => {
    if (!unitId) return;
    setDepositSavingId(unitId);
    try {
      const stripeConnectIdOverride =
        value === "" || value == null ? null : value;
      await unitModel.update(unitId, { stripeConnectIdOverride });
      message.success("Deposit account saved");
      queryClient.invalidateQueries({
        queryKey: propertyKeys.units(user?.uid),
      });
    } catch (e) {
      message.error(e?.message || "Failed to save deposit account");
    } finally {
      setDepositSavingId(null);
    }
  };

  const openPropertyModal = (p) => {
    setSelectedProperty(p);
    setPropertyModalEditing(false);
    setPropertyModalVisible(true);
  };

  const closePropertyModal = () => {
    setPropertyModalVisible(false);
    setSelectedProperty(null);
    setPropertyModalEditing(false);
  };

  const handlePropertyModalSave = async (values) => {
    if (!selectedProperty?.id) return;
    setPropertyModalSaving(true);
    try {
      const stripeConnectIdOverride =
        values.stripeConnectIdOverride === "" ||
        values.stripeConnectIdOverride == null
          ? null
          : values.stripeConnectIdOverride;
      await updateProperty(selectedProperty.id, {
        repairAutoApproveLimit:
          values.repairAutoApproveLimit != null
            ? Number(values.repairAutoApproveLimit)
            : undefined,
        stripeConnectIdOverride,
        emailMaintenanceTicketDetailsToVendors:
          values.emailMaintenanceTicketDetailsToVendors === true,
      });
      message.success("Property settings saved");
      queryClient.invalidateQueries({ queryKey: ["properties", user?.uid] });
      closePropertyModal();
    } catch (e) {
      message.error(e?.message || "Failed to save property settings");
    } finally {
      setPropertyModalSaving(false);
    }
  };

  const handleAddDepositAccount = async () => {
    const name = (newAccountName || "").trim() || "Deposit account";
    setAddAccountLoading(true);
    try {
      const { accountId, onboardingUrl } =
        await createAdditionalConnectAccount(name);
      const baseUrl = window.location.origin;
      const returnUrl = `${baseUrl}/settings#payment-setup`;
      const refreshUrl = `${baseUrl}/settings#payment-setup`;
      let url = onboardingUrl;
      if (!url) {
        const linkRes = await createAccountLink(
          accountId,
          refreshUrl,
          returnUrl
        );
        url = linkRes?.url;
      }
      if (url) {
        setShowAddAccountModal(false);
        setNewAccountName("");
        message.info("Redirecting to Stripe to link your bank account…");
        window.location.href = url;
      } else {
        throw new Error("No onboarding URL returned");
      }
    } catch (e) {
      message.error(e?.message || "Failed to add deposit account");
    } finally {
      setAddAccountLoading(false);
    }
  };

  const openRenameAccount = (account) => {
    setEditingAccountId(account.id);
    setEditingAccountName(account.name || "");
  };

  const handleRenameAccount = async () => {
    if (!editingAccountId || !editingAccountName?.trim()) return;
    setRenameAccountLoading(true);
    try {
      await updateConnectAccount(editingAccountId, {
        name: editingAccountName.trim(),
      });
      message.success("Deposit account renamed");
      queryClient.invalidateQueries({
        queryKey: ["connectAccounts", user?.uid],
      });
      setEditingAccountId(null);
      setEditingAccountName("");
    } catch (e) {
      message.error(e?.message || "Failed to rename");
    } finally {
      setRenameAccountLoading(false);
    }
  };

  const handleRemoveAccount = (account) => {
    if (account.isPrimary) return;
    setAccountToRemove(account);
  };

  const confirmRemoveAccount = async () => {
    if (!accountToRemove) return;
    setRemoveAccountLoading(true);
    try {
      await deleteConnectAccount(accountToRemove.id);
      message.success("Deposit account removed");
      queryClient.invalidateQueries({
        queryKey: ["connectAccounts", user?.uid],
      });
      queryClient.invalidateQueries({ queryKey: ["properties", user?.uid] });
      setAccountToRemove(null);
    } catch (e) {
      message.error(e?.message || "Failed to remove");
    } finally {
      setRemoveAccountLoading(false);
    }
  };

  // Load settings using useQuery for better caching and performance
  const { data: userSettings, isLoading: loading } = useQuery({
    queryKey: propertyKeys.settings(user?.uid),
    queryFn: () => SettingsModel.getOrCreateSettings(),
    enabled: !!user?.uid,
  });

  // Check Connect status for real-time payment capability
  const {
    connectStatus,
    canAcceptPayments,
    isFullyOnboarded,
    showConnectButton,
    alertMessage,
    alertType,
    loading: connectStatusLoading,
  } = useConnectStatus(user?.uid);

  // Same asset list Door settings uses: properties when present, else doors
  const depositLinkedAssets =
    displayProperties.length > 0 ? displayProperties : units;
  const getDepositAssetCount = (acc) => {
    if (!acc) return 0;
    return depositLinkedAssets.filter((asset) => {
      const override = asset?.stripeConnectIdOverride;
      const hasOverride =
        override != null && String(override).trim() !== "";
      if (acc.isPrimary) {
        return (
          !hasOverride ||
          override === acc.stripeConnectId ||
          override === connectStatus?.rawStatus?.id
        );
      }
      return hasOverride && override === acc.stripeConnectId;
    }).length;
  };

  // Handle scroll to payment setup section when coming from Rent Dashboard /
  // first-time modal. Wait until settings finish loading so we hit the real
  // section, not the skeleton.
  useEffect(() => {
    if (location.hash !== "#payment-setup" || loading || isOpsChatOnlyUser) {
      return;
    }

    let cancelled = false;
    const scrollToPaymentSetup = () => {
      if (cancelled) return;
      const element = document.getElementById("payment-setup");
      if (!element) return;
      const navOffset = window.innerWidth <= 768 ? 76 : 88;
      const y =
        element.getBoundingClientRect().top + window.scrollY - navOffset;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    };

    // Allow the real Payment Setup card to paint after loading flips false.
    const timeoutId = window.setTimeout(() => {
      requestAnimationFrame(scrollToPaymentSetup);
    }, 50);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [location.hash, loading, isOpsChatOnlyUser]);

  // Handle scroll to marketing section
  // Commented out since marketing section is commented out
  /*
 useEffect(() => {
 if (location.hash === '#marketing') {
 setTimeout(() => {
 const element = document.getElementById('marketing');
 if (element) {
 element.scrollIntoView({ 
 behavior: 'smooth', 
 block: 'start',
 inline: 'nearest'
 });
 }
 }, 100);
 }
 }, [location.hash]);
 */

  // Handle billing portal return
  useEffect(() => {
    if (location.search.includes("billing=updated")) {
      message.success("Billing information updated successfully");
      // Refresh user data
      queryClient.invalidateQueries(["user", user?.uid]);
      // Clean URL
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, queryClient, user?.uid, navigate]);

  const handleSettingChange = async (category, key, value) => {
    if (!user?.uid || !userSettings) return;

    try {
      setSaving(true);

      // Optimistically update the UI immediately
      const optimisticSettings = {
        ...userSettings,
        [category]: {
          ...(userSettings[category] || {}),
          [key]: value,
        },
      };

      // Update the cache immediately for instant UI feedback
      queryClient.setQueryData(
        propertyKeys.settings(user.uid),
        optimisticSettings
      );

      // Update in Firebase
      const updates = {
        [category]: {
          ...(userSettings[category] || {}),
          [key]: value,
        },
      };

      await SettingsModel.updateSettings(user.uid, updates);

      // Track setting change
      trackEvent("setting_change", {
        category,
        key,
        value:
          typeof value === "boolean" ? (value ? "enabled" : "disabled") : value,
      });

      // Show notification for the setting change
      const settingLabels = {
        notifications: {
          rentReminders: "Rent due alerts",
          repairUpdates: "Repair progress updates",
          leaseExpirations: "Lease expiration reminders",
          leaseExpirationAlert90: "Lease expiration email (90 days)",
          leaseExpirationAlert60: "Lease expiration email (60 days)",
          leaseExpirationAlert30: "Lease expiration email (30 days)",
          maintenanceAlerts: "Maintenance alerts",
          tenantMessages: "Tenant message alerts",
          paymentConfirmations: "Payment confirmations",
          leoDigestEnabled: "Leo AI summary emails",
          leoDigestFrequency: "Leo AI summary frequency",
        },
        automation: {
          autoRentReminders: "Automatic rent reminders",
          autoMaintenanceFollowup: "Automatic maintenance follow-up",
          autoLeaseRenewal: "Automatic lease renewal nudges",
          autoPaymentReceipts: "Automatic payment receipts",
        },
        privacy: {
          allowMarketing: "Marketing emails",
          allowSurveys: "Product surveys & feedback",
          dataSharing: "Data sharing for improvements",
          analyticsTracking: "Usage analytics tracking",
        },
        leoAuthorizations: {
          sendMessages: "Allow Leo AI to message tenants",
          sendReminders: "Allow Leo AI to send reminders",
          coordinateMaintenance: "Allow Leo AI to coordinate maintenance",
          generateReports: "Allow Leo AI to build reports",
          autoLeaseRenewal: "Allow Leo AI to send renewal reminders",
          autoPaymentReceipts: "Allow Leo AI to send payment receipts",
        },
        portfolio: {
          monthlyDebtService: "Monthly debt service",
        },
        preferences: {
          demoModePortfolio: "Demo portfolio on dashboard",
        },
      };

      const settingLabel = settingLabels[category]?.[key] || key;
      const actionText =
        typeof value === "boolean"
          ? value
            ? "turned on"
            : "turned off"
          : `updated to "${value}"`;
      message.success(`${settingLabel} ${actionText}`);
    } catch (error) {
      console.error("Error updating setting:", error);
      message.error("Failed to update setting");

      // Revert the optimistic update on error
      queryClient.setQueryData(propertyKeys.settings(user.uid), userSettings);
    } finally {
      setSaving(false);
    }
  };

  const handleManageStripeAccount = async () => {
    try {
      setManagingStripeAccount(true);

      // Get Connect account ID from connectStatus or userSettings
      const accountId =
        connectStatus?.rawStatus?.id ||
        userSettings?.paymentSetup?.stripeAccountId;

      if (!accountId) {
        message.error(
          "Stripe account not found. Please complete payment setup first."
        );
        return;
      }

      // Provide return URLs for Settings page context
      // Settings uses hash anchors to scroll to sections
      const baseUrl = window.location.origin;
      const returnUrl = `${baseUrl}/settings#payment-setup`;
      const refreshUrl = `${baseUrl}/settings#payment-setup`;

      const linkResponse = await createLoginLink(
        accountId,
        returnUrl,
        refreshUrl
      );

      if (linkResponse?.url) {
        // Show message if onboarding link was created instead of login link
        if (linkResponse.isOnboardingLink) {
          message.info(
            "Please complete your account setup. Redirecting to Stripe..."
          );
        }

        // Redirect to Stripe Dashboard or onboarding
        window.location.href = linkResponse.url;
      } else {
        throw new Error("Failed to get Stripe Dashboard link");
      }
    } catch (error) {
      console.error("Error creating Stripe login link:", error);
      message.error(
        error.message || "Failed to open Stripe Dashboard. Please try again."
      );
    } finally {
      setManagingStripeAccount(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.uid) return;

    try {
      setDeletingAccount(true);

      trackEvent("account_deletion_started", {
        user_id: user.uid,
      });

      // Call backend API to delete account
      const API_URL =
        process.env.REACT_APP_API_URL || "http://localhost:4000/api";
      if (!API_URL) {
        throw new Error("API URL not configured");
      }

      const response = await fetch(`${API_URL}/account/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
        body: JSON.stringify({
          userId: user.uid,
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          errorData.error || `Failed to delete account: ${response.status}`
        );
      }

      trackEvent("account_deletion_completed", {
        user_id: user.uid,
      });

      // Logout and redirect to home
      await logoutUser();
      navigate("/");
      message.success("Your account has been permanently deleted.");
    } catch (error) {
      console.error("Error deleting account:", error);
      message.error(
        error.message ||
          "Failed to delete account. Please try again or contact support."
      );
      trackEvent("account_deletion_failed", {
        user_id: user?.uid,
        error: error.message,
      });
    } finally {
      setDeletingAccount(false);
      setDeleteAccountStep(0);
      setDeleteConfirmationText("");
    }
  };

  if (isOpsChatOnlyUser) {
    const opsChatHasKnowledgeBase = hasLeoKnowledgeBaseContext(
      userInfo,
      unitsFromApi
    );
    const opsChatHasUnit = unitsFromApi && unitsFromApi.length > 0;
    const opsChatSetupSteps = [
      {
        key: "unit",
        num: 1,
        done: opsChatHasUnit,
        title: "Add your first door",
        body: "Every tenant gets their own private chat link. You need at least one door on Doors to generate a link and attach a knowledge base.",
        action: "Open Doors",
        path: "/units",
      },
      {
        key: "knowledgeBase",
        num: 2,
        done: opsChatHasKnowledgeBase,
        title: "Upload your lease & house rules",
        body: "Leo AI answers from your policies. The more context you add, the better it answers tenant questions.",
        action: "Open Knowledge Base",
        path: "#knowledge-base",
      },
      {
        key: "chatLink",
        num: 3,
        done: opsChatHasUnit,
        title: "Copy & share your chat link",
        body: "In Tenant Chat below, turn on chat for your property, copy the magic link, and send it to your tenant. Leo starts immediately.",
        action: "View your link",
        path: "#tenant-chat",
      },
    ];
    const opsChatDoneCount = opsChatSetupSteps.filter((s) => s.done).length;
    const opsChatAllDone = opsChatDoneCount === opsChatSetupSteps.length;

    return (
      <Container className="settings-container">
        <PageHeader
          title="OpsChat"
          subtitle="Leo AI tenant chat: add your doors, configure your knowledge base, share per-door chat links, and manage messaging."
          icon={MessageOutlined}
          subtitleOnOneLine
        />

        {/* Setup progress (visible until all steps complete) */}
        {!opsChatAllDone && (
          <OpsChatSetupCard>
            <OpsChatSetupHeader>
              <OpsChatSetupTitle>Get OpsChat running</OpsChatSetupTitle>
              <OpsChatSetupBadge>
                {opsChatDoneCount} / {opsChatSetupSteps.length} done
              </OpsChatSetupBadge>
            </OpsChatSetupHeader>
            <OpsChatStepList>
              {opsChatSetupSteps.map((step) => (
                <OpsChatStep key={step.key} $done={step.done}>
                  <OpsChatStepNum $done={step.done}>
                    {step.done ? <CheckCircleFilled /> : step.num}
                  </OpsChatStepNum>
                  <OpsChatStepBody>
                    <OpsChatStepTitle $done={step.done}>
                      {step.title}
                    </OpsChatStepTitle>
                    <OpsChatStepText>{step.body}</OpsChatStepText>
                    {!step.done && (
                      <OpsChatStepAction
                        type="button"
                        onClick={() => {
                          if (step.path.startsWith("#")) {
                            document
                              .getElementById(step.path.slice(1))
                              ?.scrollIntoView({ behavior: "smooth" });
                          } else {
                            navigate(step.path);
                          }
                        }}
                      >
                        {step.action} →
                      </OpsChatStepAction>
                    )}
                  </OpsChatStepBody>
                </OpsChatStep>
              ))}
            </OpsChatStepList>
          </OpsChatSetupCard>
        )}

        <SettingsGrid>
          <SettingsCard id="tenant-chat" className="section-card">
            <div
              id="knowledge-base"
              style={{ scrollMarginTop: "5.5rem" }}
              aria-hidden
            />
            <CardHeader className="section-card-header">
              <img
                src="/icons/leo/leo.png"
                alt="Leo AI"
                style={{ width: "24px", height: "24px", flexShrink: 0 }}
              />
              <div>
                <CardTitle>Tenant Chat (Leo AI)</CardTitle>
                <CardDescription>
                  Add doors on the Doors page, then manage your knowledge base,
                  generate per-unit magic links, and configure how Leo AI responds
                  to your tenants.
                </CardDescription>
              </div>
            </CardHeader>
            <TenantChatSettings />
          </SettingsCard>
        </SettingsGrid>
      </Container>
    );
  }

  return (
    <Container className="settings-container">
      <PageHeader
        title="Settings"
        subtitle={`Customize your property management experience`}
        icon={SettingOutlined}
      />

      {loading ? (
        <SettingsGrid>
          {/* Leo Authorizations Skeleton */}
          <SettingsCard className="section-card leo-authorizations">
            <CardHeader className="section-card-header">
              <Skeleton.Avatar
                active
                size={24}
                shape="square"
                style={{ backgroundColor: "#e2e8f0" }}
              />
              <div style={{ flex: 1 }}>
                <Skeleton
                  active
                  paragraph={{ rows: 0 }}
                  title={{ width: 200 }}
                />
                <Skeleton
                  active
                  paragraph={{ rows: 1, width: 300 }}
                  style={{ marginTop: "8px" }}
                />
              </div>
            </CardHeader>
            <LeoAuthorizationsGrid>
              {[1, 2, 3].map((i) => (
                <SettingItem key={i}>
                  <SettingContent>
                    <Skeleton
                      active
                      paragraph={{ rows: 0 }}
                      title={{ width: 180 }}
                    />
                    <Skeleton
                      active
                      paragraph={{ rows: 1, width: 250 }}
                      style={{ marginTop: "8px" }}
                    />
                  </SettingContent>
                  <Skeleton.Button
                    active
                    size="default"
                    shape="round"
                    style={{ width: 44, height: 22 }}
                  />
                </SettingItem>
              ))}
            </LeoAuthorizationsGrid>
          </SettingsCard>

          {/* Payment Setup Skeleton */}
          <SettingsCard
            className="section-card payment-setup"
          >
            <CardHeader className="section-card-header">
              <Skeleton.Avatar
                active
                size={24}
                shape="square"
                style={{ backgroundColor: "#e2e8f0" }}
              />
              <div style={{ flex: 1 }}>
                <Skeleton
                  active
                  paragraph={{ rows: 0 }}
                  title={{ width: 150 }}
                />
                <Skeleton
                  active
                  paragraph={{ rows: 1, width: 280 }}
                  style={{ marginTop: "8px" }}
                />
              </div>
            </CardHeader>
            <PaymentSetupContent>
              <Skeleton
                active
                paragraph={{ rows: 3, width: "100%" }}
                style={{ marginBottom: "1.5rem" }}
              />
              <Skeleton.Button
                active
                size="large"
                style={{ width: 200, height: 40 }}
              />
            </PaymentSetupContent>
          </SettingsCard>

          {/* Other Settings Skeleton */}
          <OtherSettingsGrid>
            {[1, 2, 3].map((i) => (
              <SettingsCard key={i} className="section-card">
                <CardHeader className="section-card-header">
                  <Skeleton.Avatar
                    active
                    size={24}
                    shape="square"
                    style={{ backgroundColor: "#e2e8f0" }}
                  />
                  <div style={{ flex: 1 }}>
                    <Skeleton
                      active
                      paragraph={{ rows: 0 }}
                      title={{ width: 120 }}
                    />
                    <Skeleton
                      active
                      paragraph={{ rows: 1, width: 200 }}
                      style={{ marginTop: "8px" }}
                    />
                  </div>
                </CardHeader>
                <div style={{ padding: "1.5rem" }}>
                  <Skeleton active paragraph={{ rows: 3 }} />
                </div>
              </SettingsCard>
            ))}
          </OtherSettingsGrid>
        </SettingsGrid>
      ) : (
        <SettingsGrid>
          {/* Leo AI Authorizations - Full Width */}
          <SettingsCard className="section-card leo-authorizations">
            <CardHeader className="section-card-header">
              <img
                src="/icons/leo/leo.png"
                alt="Leo AI"
                style={{
                  width: "24px",
                  height: "24px",
                }}
              />
              <div>
                <CardTitle
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "nowrap",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    Leo AI authorizations
                  </span>
                  <Tooltip
                    title={
                      <div style={{ maxWidth: "300px" }}>
                        <div
                          style={{
                            fontWeight: 600,
                            marginBottom: "8px",
                            color: "var(--color-primary)",
                          }}
                        >
                          Meet Leo AI, your AI door manager
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            lineHeight: "1.4",
                            color: "var(--color-text)",
                          }}
                        >
                          Leo AI is your intelligent assistant that automates
                          routine property management tasks, saving you hours
                          each week. From sending rent reminders to coordinating
                          maintenance, Leo AI handles the day-to-day operations so
                          you can focus on growing your business.
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            marginTop: "8px",
                            color: "var(--color-text-light)",
                          }}
                        >
                          💡 <strong>Pro tip:</strong> Start with a few
                          authorizations and gradually expand as you see the
                          benefits!
                        </div>
                      </div>
                    }
                    placement="right"
                    overlayStyle={{ maxWidth: "350px" }}
                  >
                    <InfoCircleOutlined
                      style={{
                        fontSize: "16px",
                        color: "rgba(255, 255, 255, 0.92)",
                        cursor: "pointer",
                        marginLeft: "8px",
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                </CardTitle>
                <CardDescription>
                  Control what your AI assistant can do on your behalf
                </CardDescription>
              </div>
            </CardHeader>

            <LeoAuthorizationsGrid>
              <SettingItem>
                <SettingContent>
                  <SettingLabel>Send Messages & Reminders</SettingLabel>
                  <SettingHelp>
                    Allow Leo AI to send messages and rent reminders to tenants
                  </SettingHelp>
                </SettingContent>
                <Switch
                  checked={userSettings?.leoAuthorizations?.sendMessages}
                  onChange={(checked) =>
                    handleSettingChange(
                      "leoAuthorizations",
                      "sendMessages",
                      checked
                    )
                  }
                  loading={saving}
                />
              </SettingItem>

              <SettingItem>
                <SettingContent>
                  <SettingLabel>Coordinate Maintenance</SettingLabel>
                  <SettingHelp>
                    Allow Leo AI to coordinate with contractors and schedule
                    repairs
                  </SettingHelp>
                </SettingContent>
                <Switch
                  checked={
                    userSettings?.leoAuthorizations?.coordinateMaintenance
                  }
                  onChange={(checked) =>
                    handleSettingChange(
                      "leoAuthorizations",
                      "coordinateMaintenance",
                      checked
                    )
                  }
                  loading={saving}
                />
              </SettingItem>

              <SettingItem>
                <SettingContent>
                  <SettingLabel>Generate Reports</SettingLabel>
                  <SettingHelp>
                    Allow Leo AI to generate monthly reports and analytics
                  </SettingHelp>
                </SettingContent>
                <Switch
                  checked={userSettings?.leoAuthorizations?.generateReports}
                  onChange={(checked) =>
                    handleSettingChange(
                      "leoAuthorizations",
                      "generateReports",
                      checked
                    )
                  }
                  loading={saving}
                />
              </SettingItem>
            </LeoAuthorizationsGrid>
          </SettingsCard>

          <ComplianceSettings properties={displayProperties} units={units} />

          <SettingsCard id="tenant-chat" className="section-card">
            <CardHeader className="section-card-header">
              <MessageOutlined
                style={{ fontSize: "24px" }}
              />
              <div>
                <CardTitle>Private Tenant Chat (Leo AI)</CardTitle>
                <CardDescription>
                  Tenants automatically receive their private Leo chat link by
                  email when added. Manage each door&apos;s knowledge base and
                  chat link below.
                </CardDescription>
              </div>
            </CardHeader>
            <TenantChatSettings />
          </SettingsCard>

          <SettingsCard id="import-portfolio" className="section-card">
            <CardHeader className="section-card-header">
              <CloudUploadOutlined
                style={{ fontSize: "24px" }}
              />
              <div>
                <CardTitle>Import portfolio</CardTitle>
                <CardDescription>
                  Bulk-import a rent roll CSV. New rows only. Duplicate emails
                  or doors are skipped.
                </CardDescription>
              </div>
            </CardHeader>
            <div
              style={{
                padding: "0 1.5rem 1.5rem",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <ImportPortfolioNotice $variant="success">
                <SafetyOutlined
                  style={{
                    fontSize: 24,
                    color: "var(--color-success, #2e7d32)",
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                  aria-hidden
                />
                <div>
                  <ImportPortfolioNoticeTitle $variant="success">
                    Your existing data stays safe
                  </ImportPortfolioNoticeTitle>
                  <ImportPortfolioNoticeBody $variant="success">
                    This import only <strong>adds</strong> new records. We never
                    overwrite properties, doors, or tenants. Up to{" "}
                    <strong>2,000</strong> data rows per file.
                  </ImportPortfolioNoticeBody>
                </div>
              </ImportPortfolioNotice>
              <ImportPortfolioNotice
                $variant={canAddMoreUnitsForImport ? "info" : "warning"}
              >
                {canAddMoreUnitsForImport ? (
                  <InfoCircleOutlined
                    style={{
                      fontSize: 24,
                      color: "#1565c0",
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                    aria-hidden
                  />
                ) : (
                  <ExclamationCircleOutlined
                    style={{
                      fontSize: 24,
                      color: "#e65100",
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                    aria-hidden
                  />
                )}
                <div>
                  <ImportPortfolioNoticeTitle
                    $variant={canAddMoreUnitsForImport ? "info" : "warning"}
                  >
                    {canAddMoreUnitsForImport
                      ? `Plan limit: up to ${importUnitCap} doors`
                      : `Plan limit reached (${importUnitCap} doors)`}
                  </ImportPortfolioNoticeTitle>
                  <ImportPortfolioNoticeBody
                    $variant={canAddMoreUnitsForImport ? "info" : "warning"}
                  >
                    {canAddMoreUnitsForImport ? (
                      <>
                        You have <strong>{units.length}</strong> door
                        {units.length !== 1 ? "s" : ""} and can add{" "}
                        <strong>{importRemainingSlots}</strong> more. New doors
                        from the CSV count toward this limit (same as on the
                        Doors page).
                      </>
                    ) : (
                      <>
                        New doors from the import are skipped until you upgrade
                        or free capacity. You can still run an import. The
                        summary lists skipped rows.{" "}
                        <Link to="/profile">Review your plan in Profile</Link>{" "}
                        to upgrade.
                      </>
                    )}
                  </ImportPortfolioNoticeBody>
                </div>
              </ImportPortfolioNotice>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.625rem",
                  width: "100%",
                  paddingTop: "0.25rem",
                }}
              >
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={
                    <UploadOutlined
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                  }
                  onClick={() => setIsImportPortfolioModalOpen(true)}
                  style={{
                    height: 48,
                    fontWeight: 600,
                    borderRadius: 8,
                  }}
                >
                  Start CSV import
                </Button>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8125rem",
                    lineHeight: 1.45,
                    color: "var(--color-text-muted, #94a3b8)",
                    textAlign: "center",
                  }}
                >
                  Run another import anytime from Settings or the Dashboard.
                </p>
              </div>
            </div>
          </SettingsCard>

          {/* Door / property settings: deposit account */}
          <SettingsCard id="door-settings" className="section-card">
            <CardHeader className="section-card-header">
              <HomeOutlined
                style={{ fontSize: "24px" }}
              />
              <div>
                <CardTitle>Door settings</CardTitle>
                <CardDescription>
                  Link each property to a bank account: choose which deposit
                  account (Primary or LLC) receives rent.
                </CardDescription>
              </div>
            </CardHeader>
            <div style={{ padding: "0 1.5rem 1.5rem" }}>
              {displayProperties.length === 0 ? (
                units.length === 0 ? (
                  <NoAssetsAlertWrap>
                    <Alert
                      type="info"
                      showIcon
                      message="No doors found"
                      description={
                        <>
                          This is where you link each property to a bank account
                          (&quot;Rent deposits to&quot;). Properties are loaded
                          from your account. If you don&apos;t see any, your
                          account may not have properties set up yet, or the
                          list may still be loading. Once properties appear
                          here, each row will have a{" "}
                          <strong>Rent deposits to</strong> dropdown to choose
                          Primary account or an LLC.
                        </>
                      }
                    />
                  </NoAssetsAlertWrap>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    {isSettingsDemo &&
                      (settingsDemoMode === "assets" ||
                        settingsDemoMode === "both") && (
                        <Alert
                          type="info"
                          message="Demo: multiple doors"
                          description="Sample doors with different deposit account assignments (Primary, Sunshine LLC, Riverside LLC)."
                          style={{ marginBottom: 8 }}
                        />
                      )}
                    <div
                      style={{
                        fontSize: "0.9rem",
                        color: "var(--color-text-light)",
                        marginBottom: 4,
                      }}
                    >
                      Showing your {units.length} door
                      {units.length !== 1 ? "s" : ""} from the Doors page. Link
                      each to the bank account that should receive rent
                      (default: Primary account).
                    </div>
                    <AssetCardsGrid>
                      {units.map((u) => {
                        const primaryAccountId =
                          connectStatus?.rawStatus?.id ||
                          userSettings?.paymentSetup?.stripeAccountId ||
                          (isSettingsDemo ? "demo-primary" : undefined);
                        const depositValue =
                          u.stripeConnectIdOverride != null &&
                          u.stripeConnectIdOverride !== ""
                            ? u.stripeConnectIdOverride
                            : "";
                        const unitLabel =
                          u.name ||
                          u.address_street ||
                          u.address ||
                          (typeof u.address === "object" &&
                            u.address?.street) ||
                          u.id;
                        const addressLine =
                          u.address_street ||
                          u.address_city ||
                          u.address_state ||
                          u.address_zip
                            ? [
                                u.address_street,
                                u.address_city,
                                u.address_state,
                                u.address_zip,
                              ]
                                .filter(Boolean)
                                .join(", ")
                            : typeof u.address === "object" && u.address
                            ? [
                                u.address.street || u.address.address,
                                u.address.city,
                                u.address.state,
                                u.address.zipCode,
                              ]
                                .filter(Boolean)
                                .join(", ")
                            : typeof u.address === "string"
                            ? u.address
                            : null;
                        const details = [];
                        const rent = u.monthlyRent ?? u.baseRent;
                        if (rent != null && rent > 0)
                          details.push(
                            `Rent: $${Number(rent).toLocaleString()}`
                          );
                        if (u.bedrooms != null && u.bedrooms > 0)
                          details.push(`${u.bedrooms} bed`);
                        if (u.bathrooms != null && u.bathrooms > 0)
                          details.push(`${u.bathrooms} bath`);
                        if (u.unitType)
                          details.push(u.unitType.replace(/_/g, " "));
                        const tenant =
                          tenants.find((t) => t.unitId === u.id) ||
                          (u.tenantId
                            ? tenants.find((t) => t.id === u.tenantId)
                            : null);
                        const tenantName =
                          tenant?.name ||
                          (tenant
                            ? [tenant.firstName, tenant.lastName]
                                .filter(Boolean)
                                .join(" ")
                            : null);
                        return (
                          <AssetCard key={u.id}>
                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.75rem",
                              }}
                            >
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "0.25rem",
                                }}
                              >
                                <span
                                  style={{ fontWeight: 600, fontSize: "1rem" }}
                                >
                                  Door {unitLabel}
                                </span>
                                {addressLine && (
                                  <span
                                    style={{
                                      fontSize: "0.875rem",
                                      color: "var(--color-text-light)",
                                    }}
                                  >
                                    {addressLine}
                                  </span>
                                )}
                                {tenantName && (
                                  <span
                                    style={{
                                      fontSize: "0.875rem",
                                      color: "var(--color-text)",
                                    }}
                                  >
                                    Tenant: {tenantName}
                                  </span>
                                )}
                                {details.length > 0 && (
                                  <span
                                    style={{
                                      fontSize: "0.8rem",
                                      color: "var(--color-text-light)",
                                    }}
                                  >
                                    {details.join(" · ")}
                                  </span>
                                )}
                              </div>
                              <AssetDepositRow>
                                <Tooltip title="Rent for this door is deposited to the selected account. Primary account is your main Stripe Connect account.">
                                  <label className="asset-deposit-label">
                                    Rent deposits to
                                  </label>
                                </Tooltip>
                                <Select
                                  value={depositValue}
                                  onChange={(val) =>
                                    handleUnitDepositAccountChange(u.id, val)
                                  }
                                  options={depositAccountOptions}
                                  style={{ minWidth: 200, width: "100%" }}
                                  loading={depositSavingId === u.id}
                                  disabled={!primaryAccountId}
                                  placeholder="Primary account"
                                  className="asset-deposit-select"
                                />
                                {!primaryAccountId && (
                                  <span
                                    style={{
                                      fontSize: "0.85rem",
                                      color: "var(--color-text-light)",
                                      width: "100%",
                                    }}
                                  >
                                    Complete payment setup below to choose
                                    deposit account.
                                  </span>
                                )}
                              </AssetDepositRow>
                            </div>
                          </AssetCard>
                        );
                      })}
                    </AssetCardsGrid>
                  </div>
                )
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  <AssetCardsGrid>
                    {displayProperties.map((p) => {
                      const primaryAccountId =
                        connectStatus?.rawStatus?.id ||
                        userSettings?.paymentSetup?.stripeAccountId;
                      const depositOptions = depositAccountOptions;
                      const depositValue =
                        p.stripeConnectIdOverride != null &&
                        p.stripeConnectIdOverride !== ""
                          ? p.stripeConnectIdOverride
                          : "";
                      return (
                        <AssetCard key={p.id}>
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.75rem",
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.25rem",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "1rem",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    flex: "1 1 200px",
                                    fontWeight: 600,
                                    fontSize: "1rem",
                                  }}
                                >
                                  {p.address || p.id}
                                </span>
                                <Button
                                  type="default"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => openPropertyModal(p)}
                                >
                                  Edit
                                </Button>
                              </div>
                              <div style={{ display: "none" }}>
                                <label
                                  style={{
                                    whiteSpace: "nowrap",
                                    color: "var(--color-text-light)",
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  AI Auto-Approve Limit ($)
                                </label>
                                <InputNumber
                                  min={0}
                                  step={50}
                                  placeholder="None"
                                  value={
                                    autoApproveDrafts[p.id] !== undefined
                                      ? autoApproveDrafts[p.id]
                                      : p.repairAutoApproveLimit ?? undefined
                                  }
                                  onChange={(v) =>
                                    setAutoApproveDrafts((s) => ({
                                      ...s,
                                      [p.id]: v,
                                    }))
                                  }
                                />
                                <Button
                                  type="primary"
                                  size="small"
                                  loading={propertySavingId === p.id}
                                  onClick={() => {
                                    const v =
                                      autoApproveDrafts[p.id] !== undefined
                                        ? autoApproveDrafts[p.id]
                                        : p.repairAutoApproveLimit ?? 0;
                                    handleRepairAutoApproveChange(
                                      p.id,
                                      typeof v === "number" && v >= 0 ? v : 0
                                    );
                                    setAutoApproveDrafts((s) => ({
                                      ...s,
                                      [p.id]: undefined,
                                    }));
                                  }}
                                >
                                  Save
                                </Button>
                              </div>
                              <AssetDepositRow>
                                <Tooltip title="Rent for this property is deposited to the selected account. Primary account is your main Stripe Connect account.">
                                  <label className="asset-deposit-label">
                                    Rent deposits to
                                  </label>
                                </Tooltip>
                                <Select
                                  value={depositValue}
                                  onChange={(val) =>
                                    handleDepositAccountChange(p.id, val)
                                  }
                                  options={depositOptions}
                                  style={{ minWidth: 200, width: "100%" }}
                                  loading={depositSavingId === p.id}
                                  disabled={!primaryAccountId}
                                  placeholder="Primary account"
                                  className="asset-deposit-select"
                                />
                                {!primaryAccountId && (
                                  <span
                                    style={{
                                      fontSize: "0.85rem",
                                      color: "var(--color-text-light)",
                                      width: "100%",
                                    }}
                                  >
                                    Complete payment setup above to choose
                                    deposit account.
                                  </span>
                                )}
                              </AssetDepositRow>
                            </div>
                          </div>
                        </AssetCard>
                      );
                    })}
                  </AssetCardsGrid>
                </div>
              )}
            </div>
          </SettingsCard>

          {/*
 <SettingsCard id="leasing-showings" className="section-card">
 <CardHeader className="section-card-header">
 <CalendarOutlined style={{ fontSize: "24px" }} />
 <div>
 <CardTitle>Leasing</CardTitle>
 <CardDescription>Public showing links and weekly availability for each property.</CardDescription>
 </div>
 </CardHeader>
 <LeasingShowingSettings
 properties={leasingProperties}
 userUid={user?.uid}
 isLoading={isPropertiesLoading}
 />
 </SettingsCard>
 */}

          {/* Payment Setup - Full Width */}
          <SettingsCard
            id="payment-setup"
            className="section-card payment-setup"
            style={{ scrollMarginTop: "5.5rem" }}
          >
            <CardHeader className="section-card-header">
              <CreditCardOutlined
                style={{ fontSize: "24px" }}
              />
              <div>
                <CardTitle>Payment Setup</CardTitle>
                <CardDescription>
                  Configure your payment processing to start collecting rent
                </CardDescription>
              </div>
            </CardHeader>

            {shouldShowScaleUpgradeForFullOs(userInfo) && (
              <div style={{ padding: "0 1.5rem 1rem" }}>
                <Alert
                  type="info"
                  showIcon
                  message="Need more doors?"
                  description="Every plan includes rent collection, Schedule E, vendors, ledgers, and Leo. Upgrade via Billing when your portfolio grows past your current door cap."
                />
              </div>
            )}

            {connectStatusLoading ? (
              <PaymentSetupContent>
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <Spin size="large" />
                  <div
                    style={{
                      marginTop: "1rem",
                      color: "var(--color-text-light)",
                    }}
                  >
                    Checking payment setup status...
                  </div>
                </div>
              </PaymentSetupContent>
            ) : !canAcceptPayments && !isSettingsDemo ? (
              <PaymentSetupContent>
                {/* Show different alerts based on Connect status */}
                {/* Show "Under Review" only if details were submitted (user completed onboarding) */}
                {(() => {
                  const detailsSubmitted =
                    connectStatus?.rawStatus?.details_submitted || false;
                  const hasPendingRequirements =
                    connectStatus?.pendingRequirements?.length > 0;
                  const isPendingVerification =
                    connectStatus?.status === "pending_verification";
                  // Check if account exists (connected but not fully enabled)
                  const hasAccountId = !!(
                    connectStatus?.rawStatus?.id ||
                    userSettings?.paymentSetup?.stripeAccountId
                  );

                  // Show "Under Review" if:
                  // 1. User completed onboarding (details submitted) but Stripe is still reviewing, OR
                  // 2. Account exists (has account ID) but payments aren't enabled yet
                  const isUnderReview =
                    (detailsSubmitted &&
                      (connectStatus?.status === "pending" ||
                        isPendingVerification ||
                        hasPendingRequirements)) ||
                    (connectStatus?.status === "pending" &&
                      hasPendingRequirements &&
                      detailsSubmitted) ||
                    (hasAccountId && !canAcceptPayments); // If account exists but can't accept payments, show under review

                  return isUnderReview ? (
                    <PaymentSetupAlert
                      style={{
                        backgroundColor: "#fff7e6",
                        borderColor: "#ffd591",
                      }}
                    >
                      <PaymentSetupIcon style={{ color: "#fa8c16" }}>
                        <ExclamationCircleOutlined />
                      </PaymentSetupIcon>
                      <div>
                        <PaymentSetupTitle style={{ color: "#d46b08" }}>
                          Payment Processing Under Review
                        </PaymentSetupTitle>
                        <PaymentSetupDescription style={{ color: "#ad6800" }}>
                          Your payment account setup is currently being reviewed
                          by Stripe. This process typically takes a few minutes
                          to a few hours.
                          {connectStatus?.pendingRequirements?.length > 0 && (
                            <>
                              <br />
                              <br />
                              <strong>Pending requirements:</strong>
                              <ul
                                style={{
                                  margin: "8px 0 0 0",
                                  paddingLeft: "20px",
                                  color: "#ad6800",
                                }}
                              >
                                {connectStatus.pendingRequirements.map(
                                  (requirement, index) => (
                                    <li
                                      key={index}
                                      style={{ marginBottom: "4px" }}
                                    >
                                      {requirement
                                        .replace(/_/g, " ")
                                        .replace(/\b\w/g, (l) =>
                                          l.toUpperCase()
                                        )}
                                    </li>
                                  )
                                )}
                              </ul>
                            </>
                          )}
                        </PaymentSetupDescription>
                      </div>
                    </PaymentSetupAlert>
                  ) : (
                    <PaymentSetupAlert>
                      <PaymentSetupIcon>
                        <ExclamationCircleOutlined />
                      </PaymentSetupIcon>
                      <div>
                        <PaymentSetupTitle>
                          Complete Stripe Connect Onboarding
                        </PaymentSetupTitle>
                        <PaymentSetupDescription>
                          {alertMessage ||
                            "You must complete Stripe's Know Your Customer (KYC) verification to accept payments. Required: 1) Business bank account routing/account numbers, 2) Government-issued photo ID, 3) Business address and phone, 4) SSN (individual) or EIN (business), 5) Business type and industry classification. Stripe will verify your identity and business legitimacy - this is mandatory for all payment processors per federal regulations."}
                        </PaymentSetupDescription>
                      </div>
                    </PaymentSetupAlert>
                  );
                })()}

                {/* Only show setup button if user hasn't completed onboarding */}
                {(() => {
                  const detailsSubmitted =
                    connectStatus?.rawStatus?.details_submitted || false;
                  const hasPendingRequirements =
                    connectStatus?.pendingRequirements?.length > 0;
                  const isPendingVerification =
                    connectStatus?.status === "pending_verification";
                  const hasAccountId = !!(
                    connectStatus?.rawStatus?.id ||
                    userSettings?.paymentSetup?.stripeAccountId
                  );

                  // Show "Under Review" if:
                  // 1. User completed onboarding (details submitted) but Stripe is still reviewing, OR
                  // 2. Account exists but payments aren't enabled yet
                  const isUnderReview =
                    (detailsSubmitted &&
                      (connectStatus?.status === "pending" ||
                        isPendingVerification ||
                        hasPendingRequirements)) ||
                    (connectStatus?.status === "pending" &&
                      hasPendingRequirements &&
                      detailsSubmitted) ||
                    (hasAccountId &&
                      !detailsSubmitted &&
                      connectStatus?.status !== "incomplete");

                  return !isUnderReview ? (
                    <PaymentSetupButton
                      type="primary"
                      size="large"
                      onClick={() => setShowPaymentSetup(true)}
                      icon={
                        <CreditCardOutlined
                          style={{ filter: "brightness(0) invert(1)" }}
                        />
                      }
                    >
                      Set Up Payment Processing
                    </PaymentSetupButton>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <PaymentSetupButton
                        type="primary"
                        size="large"
                        onClick={() => window.location.reload()}
                        icon={
                          <ReloadOutlined
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                        }
                        style={{
                          backgroundColor: "var(--color-primary)",
                          borderColor: "var(--color-primary)",
                          color: "white",
                        }}
                      >
                        Refresh Status
                      </PaymentSetupButton>
                      {(connectStatus?.rawStatus?.id ||
                        userSettings?.paymentSetup?.stripeAccountId) && (
                        <PaymentSetupButton
                          type="primary"
                          size="large"
                          onClick={handleManageStripeAccount}
                          loading={managingStripeAccount}
                          icon={
                            <SettingOutlined
                              style={{ filter: "brightness(0) invert(1)" }}
                            />
                          }
                          style={{
                            backgroundColor: "var(--color-primary)",
                            borderColor: "var(--color-primary)",
                            color: "white",
                          }}
                        >
                          Manage Stripe Account
                        </PaymentSetupButton>
                      )}
                    </div>
                  );
                })()}
              </PaymentSetupContent>
            ) : (
              <PaymentSetupContent>
                {isSettingsDemo && (
                  <Alert
                    type="info"
                    message="Demo mode"
                    description="Showing demo data for deposit accounts and/or assets. Use ?debug=true&settings_demo=accounts for multiple accounts, settings_demo=assets for multiple doors with different deposit accounts, or settings_demo=both."
                    style={{ marginBottom: 16 }}
                  />
                )}
                <PaymentSetupSuccess>
                  <CheckCircleOutlined
                    style={{ color: "var(--color-success)", fontSize: "24px" }}
                  />
                  <div>
                    <PaymentSetupTitle>
                      Online Payments Enabled
                    </PaymentSetupTitle>
                    <PaymentSetupDescription>
                      Your payment processing is active and ready. You can
                      collect rent digitally from your tenants.
                    </PaymentSetupDescription>
                  </div>
                </PaymentSetupSuccess>

                {(accountsForOptionsAndGrid?.length > 0 ||
                  connectStatus?.rawStatus?.id ||
                  userSettings?.paymentSetup?.stripeAccountId ||
                  isSettingsDemo) && (
                  <div style={{ marginBottom: 16, textAlign: "left" }}>
                    <div
                      style={{
                        fontWeight: 600,
                        marginBottom: 8,
                        color: "var(--color-text)",
                      }}
                    >
                      Your deposit accounts
                    </div>
                    <DepositAccountsGrid>
                      {(accountsForOptionsAndGrid?.length > 0
                        ? accountsForOptionsAndGrid
                        : [
                            {
                              id: "primary",
                              name: "Primary account",
                              isPrimary: true,
                              stripeConnectId:
                                connectStatus?.rawStatus?.id ||
                                userSettings?.paymentSetup?.stripeAccountId,
                            },
                          ]
                      ).map((acc) => {
                        const linkedCount = getDepositAssetCount(acc);
                        const isActive = !!acc.connectStatus?.charges_enabled;
                        return (
                        <DepositAccountCard key={acc.id}>
                          <div className="deposit-account-main">
                            <BankOutlined
                              style={{
                                color: "var(--color-primary)",
                                flexShrink: 0,
                                marginTop: 3,
                              }}
                            />
                            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                              <span
                                className="deposit-account-name"
                                style={{ fontWeight: 500 }}
                              >
                                {acc.name}
                              </span>
                              {(acc.bankName || acc.bankLast4) && (
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "var(--color-text-light)",
                                    marginTop: 2,
                                  }}
                                >
                                  {acc.bankName || "Bank"}
                                  {acc.bankLast4 ? ` ••••${acc.bankLast4}` : ""}
                                </span>
                              )}
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "var(--color-text-light)",
                                  marginTop: 2,
                                }}
                              >
                                {linkedCount}{" "}
                                {displayProperties.length > 0
                                  ? linkedCount === 1
                                    ? "property"
                                    : "properties"
                                  : linkedCount === 1
                                  ? "door"
                                  : "doors"}
                              </span>
                            </div>
                          </div>
                          <div className="deposit-account-footer">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                flexWrap: "wrap",
                              }}
                            >
                              {acc.isPrimary && (
                                <Tag color="blue" style={{ margin: 0 }}>
                                  Default
                                </Tag>
                              )}
                              {isActive && (
                                <Tag color="success" style={{ margin: 0 }}>
                                  Active
                                </Tag>
                              )}
                            </div>
                            <DepositAccountActions>
                              <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => openRenameAccount(acc)}
                              />
                              {!acc.isPrimary && (
                                <Button
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => handleRemoveAccount(acc)}
                                />
                              )}
                            </DepositAccountActions>
                          </div>
                        </DepositAccountCard>
                        );
                      })}
                    </DepositAccountsGrid>
                  </div>
                )}

                <div
                  role="region"
                  aria-label="Link properties to bank accounts"
                  style={{
                    marginBottom: 16,
                    padding: "12px 14px",
                    background: "var(--color-bg-secondary)",
                    borderRadius: 8,
                    border: "1px solid var(--color-border-light)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: "var(--color-text)",
                      marginBottom: 6,
                    }}
                  >
                    <strong>Link a property to a bank account</strong>
                  </div>
                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: "var(--color-text-light)",
                      marginBottom: 10,
                    }}
                  >
                    To choose which account (Primary or LLC) receives rent for
                    each property, use <strong>Door settings</strong> above.
                    <AssetDepositLineBreak>
                      {" "}
                      Each property has a &quot;Rent deposits to&quot; dropdown.
                      The default is your Primary account.
                    </AssetDepositLineBreak>
                  </div>
                  <Button
                    type="default"
                    size="small"
                    icon={<HomeOutlined />}
                    onClick={() =>
                      document
                        .getElementById("door-settings")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                  >
                    Go to Door settings
                  </Button>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <PaymentSetupButton
                    type="primary"
                    size="large"
                    onClick={() => setShowPaymentSetup(true)}
                    icon={
                      <CreditCardOutlined
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                    }
                    style={{
                      backgroundColor: "var(--color-primary)",
                      borderColor: "var(--color-primary)",
                      color: "white",
                    }}
                  >
                    Manage Payment Settings
                  </PaymentSetupButton>
                  {(connectStatus?.rawStatus?.id ||
                    userSettings?.paymentSetup?.stripeAccountId) && (
                    <PaymentSetupButton
                      type="primary"
                      size="large"
                      onClick={handleManageStripeAccount}
                      loading={managingStripeAccount}
                      icon={
                        <SettingOutlined
                          style={{ filter: "brightness(0) invert(1)" }}
                        />
                      }
                      style={{
                        backgroundColor: "var(--color-primary)",
                        borderColor: "var(--color-primary)",
                        color: "white",
                      }}
                    >
                      Manage Stripe Account
                    </PaymentSetupButton>
                  )}
                  <PaymentSetupButton
                    type="primary"
                    size="large"
                    onClick={() => setShowAddAccountModal(true)}
                    icon={
                      <BankOutlined
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                    }
                    style={{
                      backgroundColor: "var(--color-primary)",
                      borderColor: "var(--color-primary)",
                      color: "white",
                    }}
                  >
                    Add deposit account (LLC)
                  </PaymentSetupButton>
                </div>
              </PaymentSetupContent>
            )}
          </SettingsCard>

          {/* Marketing & Listings - Auto-syndication to external platforms (Zillow, etc.) */}
          {/* Commented out until auto-syndication feature is implemented */}
          {/* Note: We have vacancy listing pages at /lease/:unitId, but not auto-posting to external sites yet */}
          {/* 
 <SettingsCard id="marketing" className="section-card marketing">
 <CardHeader className="section-card-header">
 <GlobalOutlined style={{ fontSize: "24px" }} />
 <div>
 <CardTitle>Marketing & Listings</CardTitle>
 <CardDescription>List your rentals on Zillow, Apartments.com, Craigslist, and more</CardDescription>
 </div>
 </CardHeader>
 
 <PaymentSetupContent>
 <PaymentSetupAlert>
 <PaymentSetupIcon>
 <GlobalOutlined />
 </PaymentSetupIcon>
 <div>
 <PaymentSetupTitle>Expand Your Reach</PaymentSetupTitle>
 <PaymentSetupDescription>
  Automatically post your rental listings to major platforms like Zillow, Apartments.com, and Craigslist. 
  Save time by managing all your listings from one place.
 </PaymentSetupDescription>
 </div>
 </PaymentSetupAlert>
 
 <PaymentSetupFeatures>
 <PaymentFeature>
 <CheckCircleOutlined style={{ color: 'var(--color-success)' }} />
 <span>Post to Zillow, Apartments.com, and Craigslist</span>
 </PaymentFeature>
 <PaymentFeature>
 <CheckCircleOutlined style={{ color: 'var(--color-success)' }} />
 <span>Sync listing details automatically</span>
 </PaymentFeature>
 <PaymentFeature>
 <CheckCircleOutlined style={{ color: 'var(--color-success)' }} />
 <span>Manage all listings from one dashboard</span>
 </PaymentFeature>
 <PaymentFeature>
 <CheckCircleOutlined style={{ color: 'var(--color-success)' }} />
 <span>Update availability in real-time</span>
 </PaymentFeature>
 </PaymentSetupFeatures>
 
 <PaymentSetupButton 
 type="primary" 
 size="large"
 onClick={() => {
 // TODO: Navigate to marketing/listings page or open setup modal
 message.info('Marketing & Listings feature coming soon!');
 }}
 icon={<GlobalOutlined style={{filter: 'brightness(0) invert(1)'}} />}
 >
 Get Started with Listings
 </PaymentSetupButton>
 </PaymentSetupContent>
 </SettingsCard>
 */}

          {/* Portfolio - Debt service for DSCR on dashboard */}
          <SettingsCard className="section-card portfolio">
            <CardHeader className="section-card-header">
              <DollarOutlined
                style={{ fontSize: "24px" }}
              />
              <div>
                <CardTitle>Portfolio</CardTitle>
                <CardDescription>
                  Total monthly debt service (all properties combined) used to
                  calculate portfolio DSCR on your dashboard
                </CardDescription>
              </div>
            </CardHeader>
            <SettingItem className="portfolio-debt-service">
              <SettingContent>
                <SettingLabel>Total monthly debt service ($)</SettingLabel>
                <SettingHelp $fullWidth>
                  Sum of all mortgage/debt payments across your portfolio. Leave
                  empty if you have no debt or get DSCR from your backend.
                </SettingHelp>
              </SettingContent>
              <DebtServiceInputWrapper>
                <InputNumber
                  min={0}
                  step={100}
                  placeholder="e.g. 2,500"
                  value={
                    userSettings?.portfolio?.monthlyDebtService ?? undefined
                  }
                  onChange={(val) => {
                    const num = val == null ? null : Number(val);
                    if (num !== null && (Number.isNaN(num) || num < 0)) return;
                    handleSettingChange("portfolio", "monthlyDebtService", num);
                  }}
                  formatter={(value) =>
                    value != null
                      ? `${Number(value).toLocaleString("en-US")}`
                      : ""
                  }
                  parser={(displayValue) =>
                    String(displayValue || "").replace(/,/g, "") || ""
                  }
                  className="debt-service-input"
                />
              </DebtServiceInputWrapper>
            </SettingItem>
          </SettingsCard>

          {/* Other Settings Grid */}
          <OtherSettingsGrid>
            {/* Notifications Settings */}
            <SettingsCard id="notifications" className="section-card">
              <CardHeader className="section-card-header">
                <BellOutlined
                  style={{ fontSize: "24px" }}
                />
                <div>
                  <CardTitle>Notifications</CardTitle>
                  <CardDescription>
                    Manage how you receive updates and alerts
                  </CardDescription>
                </div>
              </CardHeader>

              <SettingItem>
                <SettingContent>
                  <SettingLabel>Rent Reminders</SettingLabel>
                  <SettingHelp>
                    Get notified when rent is due or overdue
                  </SettingHelp>
                </SettingContent>
                <Switch
                  checked={userSettings?.notifications?.rentReminders}
                  onChange={(checked) =>
                    handleSettingChange(
                      "notifications",
                      "rentReminders",
                      checked
                    )
                  }
                  loading={saving}
                />
              </SettingItem>

              <SettingItem>
                <SettingContent>
                  <SettingLabel>Repair Updates</SettingLabel>
                  <SettingHelp>
                    Receive updates on maintenance requests and repairs
                  </SettingHelp>
                </SettingContent>
                <Switch
                  checked={userSettings?.notifications?.repairUpdates}
                  onChange={(checked) =>
                    handleSettingChange(
                      "notifications",
                      "repairUpdates",
                      checked
                    )
                  }
                  loading={saving}
                />
              </SettingItem>

              <SettingItem>
                <SettingContent>
                  <SettingLabel>Lease Expirations</SettingLabel>
                  <SettingHelp>
                    Get alerts when leases are about to expire
                  </SettingHelp>
                </SettingContent>
                <Switch
                  checked={userSettings?.notifications?.leaseExpirations}
                  onChange={(checked) =>
                    handleSettingChange(
                      "notifications",
                      "leaseExpirations",
                      checked
                    )
                  }
                  loading={saving}
                />
              </SettingItem>

              <SettingItem>
                <SettingContent>
                  <SettingLabel>Tenant Messages</SettingLabel>
                  <SettingHelp>
                    Notify when tenants send messages or requests
                  </SettingHelp>
                </SettingContent>
                <Switch
                  checked={userSettings?.notifications?.tenantMessages}
                  onChange={(checked) =>
                    handleSettingChange(
                      "notifications",
                      "tenantMessages",
                      checked
                    )
                  }
                  loading={saving}
                />
              </SettingItem>

              <SettingItem>
                <SettingContent>
                  <SettingLabel>Leo AI activity updates</SettingLabel>
                  <SettingHelp>
                    Get periodic summaries of what happened at your doors and
                    what Leo AI handled for you
                  </SettingHelp>
                  <div style={{ marginTop: "0.75rem" }}>
                    <Select
                      value={
                        userSettings?.notifications?.leoDigestFrequency ||
                        "weekly"
                      }
                      onChange={(val) =>
                        handleSettingChange(
                          "notifications",
                          "leoDigestFrequency",
                          val
                        )
                      }
                      disabled={!userSettings?.notifications?.leoDigestEnabled}
                      style={{ minWidth: 160 }}
                    >
                      <Option value="daily">Daily</Option>
                      <Option value="weekly">Weekly</Option>
                      <Option value="biweekly">Every 2 Weeks</Option>
                      <Option value="monthly">Monthly</Option>
                    </Select>
                  </div>
                </SettingContent>
                <Switch
                  checked={userSettings?.notifications?.leoDigestEnabled}
                  onChange={(checked) =>
                    handleSettingChange(
                      "notifications",
                      "leoDigestEnabled",
                      checked
                    )
                  }
                  loading={saving}
                />
              </SettingItem>
            </SettingsCard>

            {/* Automation Settings */}
            <SettingsCard className="section-card">
              <CardHeader className="section-card-header">
                <ToolOutlined
                  style={{ fontSize: "24px" }}
                />
                <div>
                  <CardTitle>Automation</CardTitle>
                  <CardDescription>
                    Let AI handle routine tasks automatically
                  </CardDescription>
                </div>
              </CardHeader>

              <SettingItem>
                <SettingContent>
                  <SettingLabel>Auto Rent Reminders</SettingLabel>
                  <SettingHelp>
                    Automatically send rent reminders to tenants
                  </SettingHelp>
                </SettingContent>
                <Switch
                  checked={userSettings?.automation?.autoRentReminders}
                  onChange={(checked) =>
                    handleSettingChange(
                      "automation",
                      "autoRentReminders",
                      checked
                    )
                  }
                  loading={saving}
                />
              </SettingItem>

              <SettingItem>
                <SettingContent>
                  <SettingLabel>Auto Maintenance Follow-up</SettingLabel>
                  <SettingHelp>
                    Automatically follow up on repair requests
                  </SettingHelp>
                </SettingContent>
                <Switch
                  checked={userSettings?.automation?.autoMaintenanceFollowup}
                  onChange={(checked) =>
                    handleSettingChange(
                      "automation",
                      "autoMaintenanceFollowup",
                      checked
                    )
                  }
                  loading={saving}
                />
              </SettingItem>

              <SettingItem>
                <SettingContent>
                  <SettingLabel>Auto Lease Renewal</SettingLabel>
                  <SettingHelp>
                    Automatically send lease renewal reminders
                  </SettingHelp>
                </SettingContent>
                <Switch
                  checked={userSettings?.automation?.autoLeaseRenewal}
                  onChange={(checked) =>
                    handleSettingChange(
                      "automation",
                      "autoLeaseRenewal",
                      checked
                    )
                  }
                  loading={saving}
                />
              </SettingItem>
            </SettingsCard>

            {/* Presentations & demo - only when ?debug=true (see getDebugMode in constants) */}
            {getDebugMode() ? (
              <SettingsCard className="section-card" id="presentations-demo">
                <CardHeader className="section-card-header">
                  <PlayCircleOutlined
                    style={{ fontSize: "24px" }}
                  />
                  <div>
                    <CardTitle>Presentations &amp; demo</CardTitle>
                    <CardDescription>
                      Show a polished sample portfolio on your Property Dashboard
                      without affecting real data
                    </CardDescription>
                  </div>
                </CardHeader>

                <SettingItem>
                  <SettingContent>
                    <SettingLabel>Demo portfolio (20 units)</SettingLabel>
                    <SettingHelp>
                      While <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px] text-slate-700">?debug=true</code> is in the URL, the Property Dashboard can show a fictional 20-door portfolio for charts and cards only. Turn this off to load your real portfolio even in debug. Units, tenants, and payments are never modified.
                    </SettingHelp>
                  </SettingContent>
                  <Switch
                    checked={
                      userSettings?.preferences?.demoModePortfolio !== false
                    }
                    onChange={(checked) =>
                      handleSettingChange(
                        "preferences",
                        "demoModePortfolio",
                        checked
                      )
                    }
                    loading={saving}
                  />
                </SettingItem>
              </SettingsCard>
            ) : null}

            {/* Privacy & Data Settings */}
            <SettingsCard className="section-card">
              <CardHeader className="section-card-header">
                <SafetyOutlined
                  style={{ fontSize: "24px" }}
                />
                <div>
                  <CardTitle>Privacy & Data</CardTitle>
                  <CardDescription>
                    Control your data and privacy preferences
                  </CardDescription>
                </div>
              </CardHeader>

              <SettingItem>
                <SettingContent>
                  <SettingLabel>Marketing Communications</SettingLabel>
                  <SettingHelp>
                    Receive updates about new features and services
                  </SettingHelp>
                </SettingContent>
                <Switch
                  checked={userSettings?.privacy?.allowMarketing}
                  onChange={(checked) =>
                    handleSettingChange("privacy", "allowMarketing", checked)
                  }
                  loading={saving}
                />
              </SettingItem>

              <SettingItem>
                <SettingContent>
                  <SettingLabel>Product Surveys & Feedback</SettingLabel>
                  <SettingHelp>
                    Help improve LandlordOps by participating in surveys and
                    providing feedback
                  </SettingHelp>
                </SettingContent>
                <Switch
                  checked={userSettings?.privacy?.allowSurveys}
                  onChange={(checked) =>
                    handleSettingChange("privacy", "allowSurveys", checked)
                  }
                  loading={saving}
                />
              </SettingItem>
            </SettingsCard>
          </OtherSettingsGrid>
        </SettingsGrid>
      )}

      {/* Delete Account Section - Hidden for now */}
      {false && (
        <DeleteAccountSection>
          <DeleteAccountCard>
            <DeleteAccountHeader>
              <ExclamationCircleOutlined
                style={{ color: "#ff4d4f", fontSize: "24px" }}
              />
              <DeleteAccountTitle>Delete Account</DeleteAccountTitle>
            </DeleteAccountHeader>
            <DeleteAccountDescription>
              Permanently delete your account and all associated data. This
              action cannot be undone.
            </DeleteAccountDescription>
            <DeleteAccountButton
              danger
              type="primary"
              icon={<ExclamationCircleOutlined />}
              onClick={() => setDeleteAccountStep(1)}
            >
              Delete My Account
            </DeleteAccountButton>
          </DeleteAccountCard>
        </DeleteAccountSection>
      )}

      {/* Payment Setup Modal - same chrome as AddAccountModal / app modals */}
      <PaymentSetupFlowModal
        title="Payment Processing Setup"
        open={showPaymentSetup}
        onCancel={() => setShowPaymentSetup(false)}
        footer={null}
        width={900}
        centered
        destroyOnClose
        closable
      >
        <ConnectOnboarding
          inModal
          landlordId={user?.uid}
          onComplete={async () => {
            try {
              // Update payment setup status in database
              await SettingsModel.updatePaymentSetup(user?.uid, {
                isComplete: true,
                onboardingComplete: true,
                bankAccountConnected: true,
              });

              // Invalidate settings query to refresh the UI
              queryClient.invalidateQueries({
                queryKey: propertyKeys.settings(user?.uid),
              });

              setShowPaymentSetup(false);
              message.success(
                "Payment processing setup completed successfully!"
              );
            } catch (error) {
              console.error("Error updating payment setup status:", error);
              message.error(
                "Setup completed but there was an error saving the status. Please refresh the page."
              );
            }
          }}
        />
      </PaymentSetupFlowModal>

      {/* Add deposit account (LLC) modal */}
      <AddAccountModal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <BankOutlined
              style={{
                filter: "brightness(0) invert(1)",
                fontSize: "18px",
                marginTop: "-2px",
              }}
            />
            <span
              style={{
                color: "white",
                fontWeight: 600,
                fontSize: "18px",
                verticalAlign: "middle",
              }}
            >
              Add deposit account (LLC)
            </span>
          </div>
        }
        open={showAddAccountModal}
        onCancel={() => {
          setShowAddAccountModal(false);
          setNewAccountName("");
        }}
        footer={
          <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
            <Button
              onClick={() => {
                setShowAddAccountModal(false);
                setNewAccountName("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              icon={
                <BankOutlined style={{ filter: "brightness(0) invert(1)" }} />
              }
              loading={addAccountLoading}
              onClick={handleAddDepositAccount}
              disabled={!newAccountName?.trim()}
              style={{
                background: "linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)",
                borderColor: "var(--color-primary)",
              }}
            >
              Link account
            </Button>
          </div>
        }
        destroyOnClose
        width={480}
        centered
      >
        <p
          style={{
            marginBottom: 20,
            color: "var(--color-text)",
            lineHeight: 1.6,
          }}
        >
          Add a separate bank account for an LLC or entity. You’ll complete
          Stripe’s verification for this account. Then you can assign it to
          properties in Door settings.
        </p>
        <Input
          placeholder="e.g. Sunset LLC, 123 Main St LLC"
          value={newAccountName}
          onChange={(e) => setNewAccountName(e.target.value)}
          onPressEnter={handleAddDepositAccount}
          size="large"
          style={{ borderRadius: 8, marginBottom: 8 }}
        />
      </AddAccountModal>

      {/* Rename deposit account modal */}
      <AddAccountModal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <EditOutlined
              style={{ filter: "brightness(0) invert(1)", fontSize: "18px" }}
            />
            <span style={{ color: "white", fontWeight: 600, fontSize: "18px" }}>
              Rename deposit account
            </span>
          </div>
        }
        open={!!editingAccountId}
        onCancel={() => {
          setEditingAccountId(null);
          setEditingAccountName("");
        }}
        footer={
          <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
            <Button
              onClick={() => {
                setEditingAccountId(null);
                setEditingAccountName("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={renameAccountLoading}
              onClick={handleRenameAccount}
              disabled={!editingAccountName?.trim()}
              style={{
                background: "linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)",
                borderColor: "var(--color-primary)",
              }}
            >
              Save
            </Button>
          </div>
        }
        destroyOnClose
        width={480}
        centered
      >
        <p
          style={{
            marginBottom: 16,
            color: "var(--color-text)",
            lineHeight: 1.5,
          }}
        >
          Change the display name for this deposit account. It will still be the
          same Stripe account.
        </p>
        <Input
          placeholder="e.g. Sunset LLC"
          value={editingAccountName}
          onChange={(e) => setEditingAccountName(e.target.value)}
          onPressEnter={handleRenameAccount}
          size="large"
          style={{ borderRadius: 8 }}
        />
      </AddAccountModal>

      {/* Remove deposit account confirmation modal */}
      <DeleteAccountModal
        title={null}
        open={!!accountToRemove}
        onCancel={() => setAccountToRemove(null)}
        footer={null}
        width={480}
        centered
        closable={true}
      >
        <DeleteAccountModalContent>
          <DeleteAccountModalHeader>
            <DeleteAccountIconWrapper>
              <ExclamationCircleOutlined />
            </DeleteAccountIconWrapper>
            <DeleteAccountModalTitle>
              Remove deposit account?
            </DeleteAccountModalTitle>
          </DeleteAccountModalHeader>
          <DeleteAccountModalBody>
            <DeleteAccountModalText>
              {accountToRemove &&
                (() => {
                  const count = properties.filter(
                    (p) =>
                      p.stripeConnectIdOverride ===
                      accountToRemove.stripeConnectId
                  ).length;
                  return count > 0 ? (
                    <>
                      <strong>"{accountToRemove.name}"</strong> is used by{" "}
                      {count} propert{count === 1 ? "y" : "ies"}. Remove it
                      anyway? You'll need to assign a different deposit account
                      in Door settings.
                    </>
                  ) : (
                    <>
                      Remove <strong>"{accountToRemove.name}"</strong>? You can
                      add it again later.
                    </>
                  );
                })()}
            </DeleteAccountModalText>
            {accountToRemove &&
              properties.some(
                (p) =>
                  p.stripeConnectIdOverride === accountToRemove.stripeConnectId
              ) && (
                <DeleteAccountModalWarning>
                  <ExclamationCircleOutlined
                    style={{ marginRight: "0.5rem" }}
                  />
                  Properties currently using this account will need to be
                  reassigned in Door settings.
                </DeleteAccountModalWarning>
              )}
          </DeleteAccountModalBody>
          <DeleteAccountModalFooter>
            <Button onClick={() => setAccountToRemove(null)}>Cancel</Button>
            <Button
              danger
              type="primary"
              onClick={confirmRemoveAccount}
              loading={removeAccountLoading}
            >
              Remove
            </Button>
          </DeleteAccountModalFooter>
        </DeleteAccountModalContent>
      </DeleteAccountModal>

      <ImportPortfolioModal
        open={isImportPortfolioModalOpen}
        onClose={() => setIsImportPortfolioModalOpen(false)}
      />

      {/* Property details / deposit account modal */}
      <DetailsModal
        open={propertyModalVisible}
        onCancel={closePropertyModal}
        title="Property settings"
        icon={<HomeOutlined />}
        data={
          selectedProperty
            ? {
                ...selectedProperty,
                stripeConnectIdOverride:
                  selectedProperty.stripeConnectIdOverride ?? "",
              }
            : null
        }
        type="property"
        loading={propertyModalSaving}
        isEditing={propertyModalEditing}
        onToggleEdit={() => setPropertyModalEditing((v) => !v)}
        onSave={handlePropertyModalSave}
        relatedData={{
          depositAccountOptions,
        }}
      />

      {/* Delete Account Confirmation Modals */}
      {/* Step 1: Initial Warning */}
      <DeleteAccountModal
        title={null}
        open={deleteAccountStep === 1}
        onCancel={() => {
          setDeleteAccountStep(0);
          setDeleteConfirmationText("");
        }}
        footer={null}
        width={600}
        centered
        closable={true}
      >
        <DeleteAccountModalContent>
          <DeleteAccountModalHeader>
            <DeleteAccountIconWrapper>
              <ExclamationCircleOutlined />
            </DeleteAccountIconWrapper>
            <DeleteAccountModalTitle>
              Are you sure you want to delete your account?
            </DeleteAccountModalTitle>
          </DeleteAccountModalHeader>
          <DeleteAccountModalBody>
            <DeleteAccountModalText>
              This action will permanently delete your account and all
              associated data, including:
            </DeleteAccountModalText>
            <DeleteAccountModalList>
              <li>All properties and doors</li>
              <li>All tenant information</li>
              <li>All payment records</li>
              <li>All maintenance tickets</li>
              <li>All conversation history</li>
              <li>Your Stripe subscription and payment methods</li>
            </DeleteAccountModalList>
            <DeleteAccountModalWarning>
              <ExclamationCircleOutlined style={{ marginRight: "0.5rem" }} />
              This action cannot be undone.
            </DeleteAccountModalWarning>
          </DeleteAccountModalBody>
          <DeleteAccountModalFooter>
            <Button
              onClick={() => {
                setDeleteAccountStep(0);
                setDeleteConfirmationText("");
              }}
            >
              Cancel
            </Button>
            <Button
              danger
              type="primary"
              onClick={() => setDeleteAccountStep(2)}
              size="large"
            >
              Continue
            </Button>
          </DeleteAccountModalFooter>
        </DeleteAccountModalContent>
      </DeleteAccountModal>

      {/* Step 2: Second Confirmation */}
      <DeleteAccountModal
        title={null}
        open={deleteAccountStep === 2}
        onCancel={() => {
          setDeleteAccountStep(0);
          setDeleteConfirmationText("");
        }}
        footer={null}
        width={600}
        centered
        closable={true}
      >
        <DeleteAccountModalContent>
          <DeleteAccountModalHeader>
            <DeleteAccountIconWrapper>
              <ExclamationCircleOutlined />
            </DeleteAccountIconWrapper>
            <DeleteAccountModalTitle>
              This will cancel your subscription
            </DeleteAccountModalTitle>
          </DeleteAccountModalHeader>
          <DeleteAccountModalBody>
            <DeleteAccountModalText>
              Deleting your account will immediately cancel any active Stripe
              subscriptions and stop all recurring payments.
            </DeleteAccountModalText>
            <DeleteAccountModalWarning>
              <ExclamationCircleOutlined style={{ marginRight: "0.5rem" }} />
              You will lose access to all features immediately.
            </DeleteAccountModalWarning>
          </DeleteAccountModalBody>
          <DeleteAccountModalFooter>
            <Button
              onClick={() => {
                setDeleteAccountStep(1);
                setDeleteConfirmationText("");
              }}
            >
              Go Back
            </Button>
            <Button
              danger
              type="primary"
              onClick={() => {
                setDeleteAccountStep(3);
                setDeleteConfirmationText("");
              }}
              size="large"
            >
              I Understand
            </Button>
          </DeleteAccountModalFooter>
        </DeleteAccountModalContent>
      </DeleteAccountModal>

      {/* Step 3: Final Confirmation */}
      <DeleteAccountModal
        title={null}
        open={deleteAccountStep === 3}
        onCancel={() => {
          setDeleteAccountStep(0);
          setDeleteConfirmationText("");
        }}
        footer={null}
        width={600}
        centered
        closable={true}
      >
        <DeleteAccountModalContent>
          <DeleteAccountModalHeader>
            <DeleteAccountIconWrapper>
              <ExclamationCircleOutlined />
            </DeleteAccountIconWrapper>
            <DeleteAccountModalTitle>
              Type "DELETE" to confirm
            </DeleteAccountModalTitle>
          </DeleteAccountModalHeader>
          <DeleteAccountModalBody>
            <DeleteAccountModalText>
              To confirm account deletion, please type <strong>DELETE</strong>{" "}
              in the field below:
            </DeleteAccountModalText>
            <DeleteAccountInput
              placeholder="Type DELETE to confirm"
              value={deleteConfirmationText}
              onChange={(e) => setDeleteConfirmationText(e.target.value)}
              size="large"
            />
            <DeleteAccountModalWarning>
              <ExclamationCircleOutlined style={{ marginRight: "0.5rem" }} />
              This is your last chance to cancel. Once you click delete, your
              account will be permanently removed.
            </DeleteAccountModalWarning>
          </DeleteAccountModalBody>
          <DeleteAccountModalFooter>
            <Button
              onClick={() => {
                setDeleteAccountStep(0);
                setDeleteConfirmationText("");
              }}
            >
              Cancel
            </Button>
            <Button
              danger
              type="primary"
              onClick={handleDeleteAccount}
              loading={deletingAccount}
              disabled={deleteConfirmationText !== "DELETE"}
              size="large"
            >
              {deletingAccount
                ? "Deleting Account..."
                : "Permanently Delete My Account"}
            </Button>
          </DeleteAccountModalFooter>
        </DeleteAccountModalContent>
      </DeleteAccountModal>
    </Container>
  );
};

export default Settings;

// Styled Components
const AssetDepositLineBreak = styled.span`
  font-size: inherit;
  @media (min-width: 768px) {
    display: block;
    margin-top: 0.5em;
  }
`;
const NoAssetsAlertWrap = styled.div`
  @media (min-width: 769px) {
    text-align: left;
    .ant-alert-message,
    .ant-alert-description {
      text-align: left;
    }
  }
`;
const AssetCardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  @media (min-width: 769px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  }
`;
const AssetCard = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 8px;
  border: 1px solid var(--color-border-medium);
  background: var(--color-bg-secondary);
  box-shadow: 0 1px 3px var(--shadow-light);
  padding: 10px 12px;
  @media (min-width: 768px) {
    padding: 20px 16px;
  }
`;
const AssetDepositRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid var(--color-border-medium);
  text-align: left;

  .asset-deposit-label {
    font-size: 0.875rem;
    color: var(--color-text-light);
    cursor: help;
    white-space: nowrap;
    margin: 0;
  }

  .asset-deposit-select.ant-select .ant-select-selector {
    font-size: 0.875rem;
    color: var(--color-text);
  }
`;
const Container = styled.div`
  min-height: 100vh;
  background: var(--color-bg-primary);
  padding: 2rem;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    padding: 1.5rem;
    width: 90vw;
  }

  /* Apply the new container class for better background */
  &.settings-container {
    background: var(--color-bg-primary) !important;
  }

  /* Vertically center Select dropdown arrows */
  .ant-select {
    .ant-select-selector {
      display: flex !important;
      align-items: center !important;
    }

    .ant-select-arrow {
      position: absolute !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin-top: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;

      .anticon {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        line-height: 1 !important;
      }
    }
  }
`;

const SettingsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.75rem;
  margin-top: 1.25rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    gap: 2rem;
    margin-top: 1rem;
  }
`;

const OtherSettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const LeoAuthorizationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  padding: 1.5rem 0;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SettingsCard = styled(Card)`
  border-radius: 16px;
  border: 2px solid var(--color-border-light);
  box-shadow: 0 6px 24px rgba(44, 95, 45, 0.08);
  background: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(44, 95, 45, 0.15);
    border-color: var(--color-primary);
  }

  .ant-card-body {
    padding: 2rem;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    .ant-card-body {
      padding: 1rem;
    }

    &:hover {
      transform: none;
    }
  }

  /* Apply the new section card styling */
  &.section-card {
    background: #ffffff !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 16px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04) !important;
    margin-bottom: 24px !important;
    overflow: hidden !important;
  }

  /* Full width for Leo authorizations on desktop */
  &.leo-authorizations {
    @media (min-width: 769px) {
      grid-column: 1 / -1;
    }

    .ant-card-body {
      padding: 2rem;
    }

    /* Add subtle background pattern for better visual separation */
    background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%) !important;
    border: 2px solid #e2e8f0 !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04) !important;

    /* Add a subtle border around the entire section */
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(
        90deg,
        var(--color-primary) 0%,
        var(--color-accent) 100%
      );
      border-radius: 16px 16px 0 0;
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--color-border-light);
  text-align: left;
  position: relative;

  /* Match PageHeader / app: green bar, white text */
  &.section-card-header {
    align-items: center !important;
    background: var(--gradient-primary) !important;
    /* Slightly more top than bottom so type clears the bar / card edge optically */
    padding: 1.75rem 2rem 1.25rem !important;
    margin: -2rem -2rem 1.5rem -2rem !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 14px 14px 0 0;
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.15);

    @media (max-width: 768px) {
      margin: -1rem -1rem 1.5rem -1rem !important;
      padding: 1.5rem 1.25rem 1.1rem !important;
    }

    h3 {
      color: #ffffff !important;
      font-size: 1.125rem !important;
      font-weight: 600 !important;
      line-height: 1.35 !important;
      letter-spacing: -0.01em;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
    }

    p {
      color: rgba(255, 255, 255, 0.92) !important;
      font-size: 0.9rem !important;
      line-height: 1.45 !important;
    }

    /*
     * Leading column icons (Leo mark, MessageOutlined, etc.): antd outline glyphs
     * often keep dark fills; force white via luminance invert.
     * Only direct children - not info icons inside the title row.
     */
    & > .anticon {
      color: #ffffff !important;
      filter: brightness(0) invert(1) !important;
    }

    h3 .anticon {
      filter: none !important;
      color: rgba(255, 255, 255, 0.92) !important;
    }

    h3 .anticon svg {
      fill: currentColor !important;
    }

    img {
      filter: brightness(0) invert(1) !important;
    }
  }
`;

const CardTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.01em;
  color: #1e293b;
  margin: 0 0 0.25rem 0;
  text-align: left;

  /* Beat globals / ant: green bar titles must stay white (incl. flex + spans) */
  .section-card-header & {
    color: #ffffff !important;
    line-height: 1.35 !important;
  }

  .section-card-header & span {
    color: inherit !important;
  }
`;

const CardDescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  line-height: 1.45;
  color: #6b7280;
  margin: 0;
  text-align: left;

  .section-card-header & {
    color: rgba(255, 255, 255, 0.92) !important;
    line-height: 1.45 !important;
  }
`;

/** Same visual language as PaymentSetupSuccess / PaymentSetupAlert (top accent bar, title & body font sizes). */
const ImportPortfolioNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  border-radius: 16px;
  padding: 2rem;
  text-align: left;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    pointer-events: none;
  }

  ${(p) =>
    p.$variant === "success" &&
    `
 background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
 border: 2px solid #4caf50;
 box-shadow: 0 4px 12px rgba(76, 175, 80, 0.15);
 &::before {
 background: linear-gradient(90deg, #2e7d32 0%, #4caf50 50%, #66bb6a 100%);
 }
 `}
  ${(p) =>
    p.$variant === "info" &&
    `
 background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
 border: 2px solid #42a5f5;
 box-shadow: 0 4px 12px rgba(66, 165, 245, 0.15);
 &::before {
 background: linear-gradient(90deg, #1565c0 0%, #42a5f5 50%, #64b5f6 100%);
 }
 `}
 ${(p) =>
    p.$variant === "warning" &&
    `
 background: linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%);
 border: 2px solid #ffb74d;
 box-shadow: 0 4px 12px rgba(255, 183, 77, 0.15);
 &::before {
 background: linear-gradient(90deg, #ff9800 0%, #ffb74d 50%, #ffc107 100%);
 }
 `}

 @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
    text-align: center;

    > div {
      width: 100%;
    }
  }
`;

const ImportPortfolioNoticeTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 0.35rem;
  font-family: var(--font-primary);
  line-height: 1.35;
  text-align: left;

  @media (max-width: 768px) {
    text-align: center;
  }

  ${(p) =>
    p.$variant === "success" &&
    `
 color: #1b5e20;
 `}
  ${(p) =>
    p.$variant === "info" &&
    `
 color: #0d47a1;
 `}
 ${(p) =>
    p.$variant === "warning" &&
    `
 color: #d46b08;
 `}
`;

const ImportPortfolioNoticeBody = styled.div`
  font-size: 0.8rem;
  line-height: 1.45;
  font-weight: 500;
  font-family: var(--font-secondary);
  text-align: left;

  @media (max-width: 768px) {
    text-align: center;
  }

  strong {
    font-size: inherit;
    font-weight: 700;
  }

  a {
    font-weight: 600;
    color: var(--color-primary);
  }

  ${(p) =>
    p.$variant === "success" &&
    `
 color: #2e7d32;
 `}
  ${(p) =>
    p.$variant === "info" &&
    `
 color: #1565c0;
 `}
 ${(p) =>
    p.$variant === "warning" &&
    `
 color: #ad6800;
 `}
`;

const SettingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  height: 100%;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.1);
    transform: translateY(-1px);
  }

  /* Monthly debt service: stacked layout on mobile, better alignment and spacing */
  &.portfolio-debt-service {
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: stretch;
      padding: 1rem 1.25rem;
      gap: 0.75rem;
      & > *:first-child {
        margin-right: 0;
      }
      & > *:first-child > *:first-child {
        margin-bottom: 0.25rem;
      }
      & > *:first-child > *:last-child {
        max-width: none;
      }
      .ant-input-number.debt-service-input {
        width: 100% !important;
        max-width: 100%;
      }
    }
  }
`;

const DebtServiceInputWrapper = styled.div`
  flex-shrink: 0;
  .ant-input-number {
    width: 180px;
  }
  @media (max-width: 768px) {
    width: 100%;
    .ant-input-number {
      width: 100% !important;
      max-width: 100%;
    }
  }
`;

const SettingLabel = styled.div`
  font-family: var(--font-secondary);
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b !important;
  margin-bottom: 0.5rem;
  text-align: left;
`;

const SettingHelp = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.85rem;
  color: #64748b !important;
  line-height: 1.5;
  text-align: left;
  max-width: ${(props) => (props.$fullWidth ? "none" : "280px")};
`;

const SettingContent = styled.div`
  flex: 1;
  text-align: left;
  margin-right: 1rem;
`;

// Payment Setup Styled Components
const PaymentSetupContent = styled.div`
  padding: 2rem;
  text-align: center;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 1rem;
    max-width: 100%;
    overflow: hidden;
  }
`;

const PaymentSetupIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #ff9800 0%, #ffb74d 100%);
  border-radius: 50%;
  box-shadow: 0 3px 8px rgba(255, 152, 0, 0.3);
  flex-shrink: 0;

  .anticon {
    font-size: 24px;
    color: white;
  }
`;

const PaymentSetupAlert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  background: linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%);
  border: 2px solid #ffb74d;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: left;
  box-shadow: 0 4px 12px rgba(255, 183, 77, 0.15);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #ff9800 0%, #ffb74d 50%, #ffc107 100%);
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1rem;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const PaymentSetupSuccess = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
  border: 2px solid #4caf50;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: left;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.15);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #2e7d32 0%, #4caf50 50%, #66bb6a 100%);
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1rem;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const PaymentSetupTitle = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #e65100;
  margin-bottom: 0.5rem;
  font-family: var(--font-primary);
`;

const PaymentSetupDescription = styled.div`
  font-size: 1rem;
  color: #bf360c;
  line-height: 1.6;
  font-weight: 500;
`;

const PaymentSetupButton = styled(Button)`
  height: 48px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  padding: 0 2rem;
  background: linear-gradient(135deg, #2e7d32 0%, #388e3c 100%);
  border: none;
  box-shadow: 0 3px 8px rgba(46, 125, 50, 0.3);
  color: white !important;

  &,
  & span,
  & .anticon {
    color: white !important;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(46, 125, 50, 0.4);

    &,
    & span,
    & .anticon {
      color: white !important;
    }
  }

  &:active {
    transform: translateY(0);
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    width: 100%;
    height: 44px;
    font-size: 0.9rem;
    padding: 0 1rem;
    max-width: calc(100vw - 2rem);
  }

  /* Extra small mobile screens */
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0 0.75rem;
    max-width: calc(100vw - 1.5rem);
  }
`;

const PaymentSetupFlowModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    padding: 0 !important;
  }

  .ant-modal-header {
    margin-bottom: 0 !important;
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    position: relative;
    min-height: 56px;
  }

  .ant-modal-title {
    color: #ffffff !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
    padding-right: 48px;
  }

  .ant-modal-close {
    color: #ffffff !important;
    font-size: 16px;
    top: 12px !important;
    right: 20px !important;
    inset-inline-end: 20px !important;
    margin: 0 !important;
    transform: none !important;
    width: 32px !important;
    height: 32px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    border-radius: 0 !important;
    border: none !important;

    &:hover {
      color: #ffffff !important;
      background: transparent !important;
      border-color: transparent !important;
    }
  }

  .ant-modal-close .anticon,
  .ant-modal-close .ant-modal-close-icon {
    color: #ffffff !important;
  }

  .ant-modal-close .anticon svg,
  .ant-modal-close .ant-modal-close-x svg {
    fill: #ffffff !important;
    color: #ffffff !important;
  }

  .ant-modal-body {
    padding: 24px 24px 20px;
    background: #ffffff;
    overflow-x: hidden;
  }
`;

const AddAccountModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    padding: 0;
  }

  .ant-modal-header {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 20px;
  }

  .ant-modal-title {
    color: white !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: 16px;
    right: 20px;
    background: transparent !important;
    border-radius: 0 !important;
    width: 32px !important;
    height: 32px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;

    &:hover {
      color: white !important;
      background: transparent !important;
      border-color: transparent !important;
    }
  }

  .ant-modal-body {
    padding: 24px 24px 20px;
    background: #ffffff;
  }

  .ant-modal-footer {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding: 16px 24px;
    background: #fafafa;
    display: flex;
    justify-content: center;
  }
`;

const DepositAccountsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
  }
`;

const DepositAccountCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  background: var(--color-bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--color-border-medium);
  box-shadow: 0 1px 3px var(--shadow-light);
  flex-wrap: nowrap;
  .deposit-account-main {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
    flex: 1;
  }
  .deposit-account-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .deposit-account-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-shrink: 0;
  }
  @media (min-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    padding: 20px 16px;
    min-height: 120px;
    .deposit-account-main {
      gap: 10px;
      flex: 1;
    }
    .deposit-account-footer {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid var(--color-border-light);
    }
  }
`;

const DepositAccountActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  .ant-btn {
    min-width: auto;
  }
  .ant-btn:hover,
  .ant-btn:focus,
  .ant-btn-dangerous:hover,
  .ant-btn-dangerous:focus,
  .ant-btn-danger:hover,
  .ant-btn-danger:focus {
    background: transparent !important;
  }
  @media ${device.mobile} {
    .ant-btn {
      padding-left: 10px;
      padding-right: 10px;
    }
  }
`;

const DeleteAccountSection = styled.div`
  margin-top: 3rem;
  margin-bottom: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #e5e7eb;
`;

const DeleteAccountCard = styled(Card)`
  border: 2px solid #fee2e2;
  border-radius: 16px;
  background: linear-gradient(135deg, #fef2f2 0%, #fff5f5 100%);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.1);

  .ant-card-body {
    padding: 2rem;
  }

  @media ${device.mobile} {
    .ant-card-body {
      padding: 1.5rem;
    }
  }
`;

const DeleteAccountHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  @media ${device.mobile} {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }
`;

const DeleteAccountTitle = styled.h3`
  margin: 0;
  color: #dc2626;
  font-size: 1.5rem;
  font-weight: 700;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    font-size: 1.25rem;
  }
`;

const DeleteAccountDescription = styled.p`
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  font-size: 0.95rem;

  @media ${device.mobile} {
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }
`;

const DeleteAccountButton = styled(Button)`
  height: 48px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  background: #dc2626;
  border-color: #dc2626;

  &:hover {
    background: #b91c1c !important;
    border-color: #b91c1c !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
  }

  @media ${device.mobile} {
    width: 100%;
    height: 44px;
  }
`;

const DeleteAccountModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    padding: 0;
  }

  .ant-modal-header {
    display: none;
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-modal-close {
    top: 16px;
    right: 16px;
    color: var(--color-text-light);
    z-index: 10;
    width: 32px;
    height: 32px;
    border-radius: 0 !important;
    background: transparent !important;
    transition: all 0.2s ease;

    &:hover {
      color: var(--color-text);
      background: transparent !important;
    }

    @media ${device.mobile} {
      top: 12px;
      right: 12px;
      width: 28px;
      height: 28px;
    }
  }

  @media ${device.mobile} {
    .ant-modal-content {
      border-radius: 16px;
      margin: 0;
      max-width: 100vw;
    }
  }
`;

const DeleteAccountModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const DeleteAccountModalHeader = styled.div`
  text-align: center;
  padding: 2rem 2rem 1.5rem;
  background: linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%);
  border-bottom: 1px solid #fecaca;

  @media ${device.mobile} {
    padding: 1.5rem 1rem 1rem;
  }
`;

const DeleteAccountIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #fee2e2;
  margin-bottom: 1rem;

  .anticon {
    font-size: 32px;
    color: #dc2626;
  }

  @media ${device.mobile} {
    width: 56px;
    height: 56px;
    margin-bottom: 0.75rem;

    .anticon {
      font-size: 28px;
    }
  }
`;

const DeleteAccountModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: #1f2937;
  font-family: var(--font-primary);
  line-height: 1.3;

  @media ${device.mobile} {
    font-size: 1.25rem;
  }
`;

const DeleteAccountModalBody = styled.div`
  padding: 2rem;
  flex: 1;

  @media ${device.mobile} {
    padding: 1.5rem 1rem;
  }
`;

const DeleteAccountModalText = styled.p`
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  font-size: 0.95rem;

  strong {
    color: #1f2937;
    font-weight: 600;
  }

  @media ${device.mobile} {
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }
`;

const DeleteAccountModalList = styled.ul`
  text-align: left;
  color: #6b7280;
  margin: 1.5rem 0;
  padding-left: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.8;

  li {
    margin-bottom: 0.5rem;
  }

  @media ${device.mobile} {
    font-size: 0.875rem;
    margin: 1rem 0;
    padding-left: 1.25rem;
  }
`;

const DeleteAccountModalWarning = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-left: 4px solid #dc2626;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin: 1.5rem 0;
  color: #991b1b;
  font-weight: 600;
  display: flex;
  align-items: center;
  font-size: 0.95rem;

  .anticon {
    color: #dc2626;
    font-size: 18px;
  }

  @media ${device.mobile} {
    padding: 0.875rem 1rem;
    font-size: 0.875rem;
    margin: 1rem 0;
  }
`;

const DeleteAccountInput = styled(Input)`
  margin-bottom: 1rem;
  height: 48px;
  border-radius: 8px;
  font-size: 1rem;

  &:focus,
  &:hover {
    border-color: #dc2626;
    box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.1);
  }

  @media ${device.mobile} {
    height: 44px;
    font-size: 0.95rem;
  }
`;

const DeleteAccountModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  background: #fafafa;

  button {
    min-width: 120px;
    height: 44px;
    border-radius: 8px;
    font-weight: 600;
  }

  button[type="button"]:not(.ant-btn-primary) {
    border-color: #d1d5db;
    color: #6b7280;

    &:hover {
      border-color: #9ca3af;
      color: #374151;
    }
  }

  @media ${device.mobile} {
    flex-direction: column-reverse;
    padding: 1rem;

    button {
      width: 100%;
      height: 48px;
    }
  }
`;

/* ─── OpsChat inline setup progress ─── */

const OpsChatSetupCard = styled.div`
  background: white;
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  text-align: left;

  @media ${device.mobile} {
    padding: 16px 14px;
    margin-bottom: 1.25rem;
  }
`;

const OpsChatSetupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  text-align: left;

  @media ${device.mobile} {
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 14px;
  }
`;

const OpsChatSetupTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--color-text);
  font-family: var(--font-primary);
  text-align: left;
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;

const OpsChatSetupBadge = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 10px;
  background: var(--color-bg-tertiary);
  color: var(--color-text-light);
`;

const OpsChatStepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
`;

const OpsChatStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  text-align: left;
  padding: 16px 16px 16px 14px;
  border-radius: 10px;
  border: 1px solid rgba(44, 95, 45, 0.12);
  background: var(--color-bg-tertiary, #f6f7f4);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.04);
  opacity: ${(p) => (p.$done ? 0.7 : 1)};

  @media (max-width: 480px) {
    gap: 12px;
    padding: 14px 12px;
  }
`;

const OpsChatStepNum = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(p) =>
    p.$done
      ? "var(--color-success-light, #d1fae5)"
      : "linear-gradient(135deg, #2C5F2D 0%, #4A7C59 100%)"};
  color: ${(p) => (p.$done ? "var(--color-success, #059669)" : "white")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
  line-height: 1;

  .anticon {
    font-size: 15px;
    line-height: 0;
  }
`;

const OpsChatStepBody = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const OpsChatStepTitle = styled.p`
  margin: 0 0 6px 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-text);
  text-decoration: ${(p) => (p.$done ? "line-through" : "none")};
  font-family: var(--font-primary);
  text-align: left;
  width: 100%;
  line-height: 1.35;
`;

const OpsChatStepText = styled.p`
  margin: 0 0 8px 0;
  font-size: 0.86rem;
  color: var(--color-text-light);
  line-height: 1.55;
  font-family: var(--font-secondary);
  text-align: left;
  width: 100%;
  max-width: 100%;
`;

const OpsChatStepAction = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  text-align: left;
  background: none;
  border: none;
  padding: 2px 0 0 0;
  margin: 0;
  cursor: pointer;
  font-size: 0.86rem;
  font-weight: 600;
  color: #2C5F2D;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-family: var(--font-primary);

  &:hover {
    color: #4a7c59;
  }
`;
